// src/pages/SkillsPage.js
import React, { useEffect, useState, useContext, useMemo } from 'react';
import {
  Box, Typography, Card, CardContent, Button, Table, TableBody, TableCell,
  TableContainer, TableHead, TableRow, Paper, Dialog, DialogTitle, DialogContent,
  DialogActions, TextField, Snackbar, Alert, IconButton, Tooltip, Tabs, Tab,
  MenuItem, Checkbox, FormControlLabel, Chip, Divider, Grid, InputAdornment, CircularProgress,
  Stack, TableSortLabel, Autocomplete
} from '@mui/material';
import {
  Add as AddIcon, Delete as DeleteIcon, Edit as EditIcon,
  Settings as SettingsIcon, Rule as RuleIcon, AccessTime as AccessTimeIcon,
  ChevronLeft as ChevronLeftIcon, ChevronRight as ChevronRightIcon, FilterList as FilterListIcon,
  PlayArrow as PlayIcon, Stop as StopIcon
} from '@mui/icons-material';
import { useForm, useFieldArray, Controller } from 'react-hook-form';
import { ApiContext } from '../contexts/ApiContext';
import AudioPathSelector from '../components/AudioPathSelector';
import { usePermissions } from '../hooks/usePermissions';

const DIALING_MODES = ['Manual','Preview','Progresivo','Predictivo','IVR','EngineAI'];
const PROGRESSIVE_MODES = ['Transferencia directa', 'Transferencia cuando contesten', 'Cartera'];
const PREDICTIVE_MODES = ['Auto', 'Auto - Ultra', 'Sobremarcacion', 'Agentes Virtuales'];
const OVERDIALING_MODES = ['Auto', 'Auto - Ultra', 'Sobremarcacion', 'Agentes Virtuales'];
const IVR_MODES = ['Blaster', 'Blaster Auto'];
const ORDER_MODES = [
  'Prioridad',
  'Fecha Carga ASC',
  'Fecha Carga DESC',
  'Intentos y Fecha Carga DESC'
];
const LOAD_SELECTION = ['Aleatoria', 'Defecto'];
const AMD_PROFILES = ['(Default)', 'AMDMOVIL', 'AMDMOVILESCOLOMBIA', 'BLASTER', 'CONTESTADOR_MAQUINA'];
const RING_TONES = ['ninguno', 'BELL', 'RING', 'TONE1', 'TONE2'];
const NEW_CALLS_MODES = ['BUSCAR,INSERTAR', 'SOLO_BUSCAR', 'SOLO_INSERTAR'];
const AUDIO_FORMATS = ['wav', 'mp3', 'gsm'];
const RECORD_FORMATS = ['wav', 'gsm', 'wav49'];
const RECORDING_MODE_OPTIONS = [
  { label: 'Solo Conversación', value: 'QUEUE' },
  { label: 'Grabación Completa', value: 'RING' }
];
const ROUTE_TYPES = ['Trunk','Dialplan'];
const ROUTE_FORMATS = ['Prefijo','Sufijo','Ninguno'];
const RULE_TYPES = ['Fax','DialError','Abandono','NumeroErroneo','NoContesta','NoVolverALlamar','MaximoIntentos','FueraDeHorario'];
const OVERFLOW_CONDS = ['TiempoMaxEspera','MaxLlamadasEnEspera','FueraDeHorario'];
const ALLOWED_DEFAULT_ACTIONS = ['Accept','Deny'];
const ALLOWED_CONDITIONS = ['Longitud <','Longitud >','Comienza con','Termina con','Contiene'];
const ALLOWED_ACTIONS = ['Accept','Deny'];
const EMPTY_CRM_FORM = {
  name: '',
  path_api: '',
  path_index: '',
  path_sales: ''
};

const COLUMN_OPTIONS = [
  { id: 'id', label: 'ID', sortable: true, filterable: true, type: 'number' },
  { id: 'skillname', label: 'Nombre', sortable: true, filterable: true, type: 'string' },
  { id: 'skilldef', label: 'Def', sortable: true, filterable: true, type: 'string' },
  { id: 'campaign_name', label: 'Campaña', sortable: true, filterable: true, type: 'string' },
  { id: 'datastructure_name', label: 'Estructura', sortable: true, filterable: true, type: 'string' },
  { id: 'dialing_mode', label: 'Modo', sortable: true, filterable: true, type: 'string' },
  { id: 'db_status', label: 'Estado', sortable: false, filterable: false, type: 'string' },
  { id: 'actions', label: 'Acciones', sortable: false, filterable: false, type: 'string', align: 'right' }
];

function a11yProps(index){ return { id:`tab-${index}`, 'aria-controls':`tabpanel-${index}` }; }

export default function SkillsPage() {
  const { skills, campaigns, datastructures, ends, apiCall, crmConfigs: crmConfigsApi } = useContext(ApiContext);
  const { canAction } = usePermissions();

  // listado
  const [list, setList] = useState([]);
  const [loading, setLoading] = useState(false);
  const [skillStatuses, setSkillStatuses] = useState({});
  const [statusLoading, setStatusLoading] = useState({});
  const [statusesLoadingAll, setStatusesLoadingAll] = useState(true);
  const [campaignFilter, setCampaignFilter] = useState(() => {
    if (typeof window === 'undefined') return '';
    return localStorage.getItem('skills_campaign_filter') || '';
  });
  const [statusFilter, setStatusFilter] = useState(() => {
    if (typeof window === 'undefined') return 'all';
    return localStorage.getItem('skills_status_filter') || 'all';
  });

  // catálogos
  const [campList, setCampList] = useState([]);
  const [dsList, setDsList] = useState([]);
  const [dialplanList, setDialplanList] = useState([]);
  const [amdList, setAmdList] = useState([]);
  const [trunkList, setTrunkList] = useState([]);
  const [endGroupsList, setEndGroupsList] = useState([]);
  const [allFinalOptions, setAllFinalOptions] = useState([]);
  const [finalsLoading, setFinalsLoading] = useState(false);
  const [crmConfigList, setCrmConfigList] = useState([]);

  // diálogo
  const [open, setOpen] = useState(false);
  const [editingSkill, setEditingSkill] = useState(null);
  const [tab, setTab] = useState(0);
  const [snack, setSnack] = useState({ open:false, message:'', severity:'success' });
  const [submitting, setSubmitting] = useState(false);
  const [deleting, setDeleting] = useState(false);
  const [crmDialogOpen, setCrmDialogOpen] = useState(false);
  const [crmForm, setCrmForm] = useState(EMPTY_CRM_FORM);
  const [crmEditingId, setCrmEditingId] = useState('');
  const [savingCrm, setSavingCrm] = useState(false);
  const [columnFilters, setColumnFilters] = useState({});
  const [sortConfig, setSortConfig] = useState({ column: null, direction: 'asc' });
  const [activeFilterColumn, setActiveFilterColumn] = useState(null);

  const show = (m,s='success') => setSnack({ open:true, message:m, severity:s });
  const closeSnack = () => setSnack(prev => ({ ...prev, open:false }));

  const handleSortChange = (column) => {
    setSortConfig(prev => {
      if (prev.column === column) {
        return { column, direction: prev.direction === 'asc' ? 'desc' : 'asc' };
      }
      return { column, direction: 'asc' };
    });
  };

  const handleColumnFilterChange = (column, value) => {
    setColumnFilters(prev => ({
      ...prev,
      [column]: value
    }));
  };

  // Persistente: filtro por campaña
  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('skills_campaign_filter', campaignFilter || '');
    }
  }, [campaignFilter]);

  useEffect(() => {
    if (typeof window !== 'undefined') {
      localStorage.setItem('skills_status_filter', statusFilter || 'all');
    }
  }, [statusFilter]);

  // Flatten all finales (leaf nodes) from a typification tree
  const collectFinalOptions = (tree = [], group) => {
    const collected = [];
    const walk = (nodes, path = []) => {
      nodes.forEach(node => {
        const nextPath = [...path, node.name || node.code || `#${node.id}`];
        if (node.is_final) {
          collected.push({
            id: node.id,
            code: node.code || String(node.id),
            name: node.name || node.code || `Final ${node.id}`,
            pathLabel: nextPath.join(' / '),
            groupId: group?.id || null,
            groupName: group?.name || 'Sin grupo',
            isActive: node.is_active !== false,
            label: `${group?.name || 'Sin grupo'} • ${nextPath.join(' / ')}${node.code ? ` (${node.code})` : ''}`
          });
        }
        if (node.children && node.children.length) {
          walk(node.children, nextPath);
        }
      });
    };
    walk(tree, []);
    return collected;
  };

  const loadFinalOptions = async (groups = []) => {
    if (!groups.length) {
      setAllFinalOptions([]);
      return;
    }
    setFinalsLoading(true);
    try {
      const trees = await Promise.all(
        groups.map(async (group) => {
          try {
            const resp = await ends.getTree(group.id, { include_inactive: true, cache: true, forceRefresh: true });
            if (resp?.success && Array.isArray(resp.data)) {
              return collectFinalOptions(resp.data, group);
            }
          } catch (error) {
            console.error('[SKILLS] Error loading ends tree for group', group?.id, error);
          }
          return [];
        })
      );
      const flattened = trees.flat().filter(Boolean);
      setAllFinalOptions(flattened);
    } catch (err) {
      console.error('[SKILLS] Error loading final options:', err);
      setAllFinalOptions([]);
    } finally {
      setFinalsLoading(false);
    }
  };

  const getColumnFilterString = (row, column) => {
    switch (column) {
      case 'id':
        return String(row.id ?? '');
      case 'skillname':
        return row.skillname || '';
      case 'skilldef':
        return row.skilldef || '';
      case 'campaign_name':
        return row.campaign_name || '';
      case 'datastructure_name':
        return row.datastructure_name || '';
      case 'dialing_mode':
        return row.dialing_mode || '';
      default:
        return '';
    }
  };

  const columnFilteredList = useMemo(() => {
    const activeFilters = Object.entries(columnFilters)
      .filter(([column, value]) => COLUMN_OPTIONS.some(opt => opt.id === column && opt.filterable) && value?.trim());
    const campaignFiltered = campaignFilter
      ? list.filter(row => String(row.idcampaign || '') === String(campaignFilter))
      : list;

    const statusFiltered = (() => {
      if (statusFilter === 'all') return campaignFiltered;
      const desired = statusFilter === 'active' ? 1 : 0;
      return campaignFiltered.filter(row => Number(row.status || 0) === desired);
    })();

    if (!activeFilters.length) {
      return statusFiltered;
    }
    return statusFiltered.filter(row =>
      activeFilters.every(([column, value]) => {
        const normalized = value.trim().toLowerCase();
        const candidate = getColumnFilterString(row, column).toLowerCase();
        return candidate.includes(normalized);
      })
    );
  }, [columnFilters, list, campaignFilter, statusFilter]);

  const sortedList = useMemo(() => {
    if (!sortConfig.column) {
      return columnFilteredList;
    }
    const columnType = COLUMN_OPTIONS.find(opt => opt.id === sortConfig.column)?.type || 'string';
    const data = [...columnFilteredList];
    const getSortValue = (row) => {
      switch (sortConfig.column) {
        case 'id':
          return Number(row.id) || 0;
        case 'skillname':
          return row.skillname || '';
        case 'skilldef':
          return row.skilldef || '';
        case 'campaign_name':
          return row.campaign_name || '';
        case 'datastructure_name':
          return row.datastructure_name || '';
        case 'dialing_mode':
          return row.dialing_mode || '';
        default:
          return '';
      }
    };
    data.sort((a, b) => {
      const valueA = getSortValue(a);
      const valueB = getSortValue(b);
      if (columnType === 'number') {
        const diff = (Number(valueA) || 0) - (Number(valueB) || 0);
        return sortConfig.direction === 'asc' ? diff : -diff;
      }
      const stringA = (valueA || '').toString().toLowerCase();
      const stringB = (valueB || '').toString().toLowerCase();
      if (stringA === stringB) return 0;
      if (stringA > stringB) {
        return sortConfig.direction === 'asc' ? 1 : -1;
      }
      return sortConfig.direction === 'asc' ? -1 : 1;
    });
    return data;
  }, [columnFilteredList, sortConfig]);

  // Navegación de tabs
  const totalTabs = 10; // Total de tabs
  
  // Función para verificar si un tab está habilitado
  const isTabEnabled = (tabIndex) => {
    switch(tabIndex) {
      case 1: return !(skillType === 'IN' && !useScript); // Grabación
      case 2: return skillType !== 'IN'; // Llamadas Salientes
      case 3: return useScript; // Script
      case 5: return skillType !== 'IN'; // Reglas
      case 9: return skillType !== 'IN'; // Números Permitidos
      default: return true;
    }
  };
  
  const goToPrevTab = () => {
    let newTab = tab - 1;
    // Buscar el tab anterior habilitado
    while (newTab >= 0 && !isTabEnabled(newTab)) {
      newTab--;
    }
    if (newTab >= 0) setTab(newTab);
  };
  
  const goToNextTab = () => {
    let newTab = tab + 1;
    // Buscar el siguiente tab habilitado
    while (newTab < totalTabs && !isTabEnabled(newTab)) {
      newTab++;
    }
    if (newTab < totalTabs) setTab(newTab);
  };

  // Navegación con teclado
  const handleKeyDown = (e) => {
    if (e.ctrlKey || e.metaKey) {
      if (e.key === 'ArrowLeft') {
        e.preventDefault();
        goToPrevTab();
      } else if (e.key === 'ArrowRight') {
        e.preventDefault();
        goToNextTab();
      }
    }
  };

  // Estado para control del slider de tabs
  const [tabSliderOffset, setTabSliderOffset] = useState(0);
  const [canSlideLeft, setCanSlideLeft] = useState(false);
  const [canSlideRight, setCanSlideRight] = useState(false);

  // Control del slider de tabs (no scroll de página)
  const slideTabsLeft = () => {
    const slideAmount = 200;
    const newOffset = Math.min(tabSliderOffset + slideAmount, 0); // Deslizar hacia la derecha (mostrar tabs de la izquierda)
    setTabSliderOffset(newOffset);
  };

  const slideTabsRight = () => {
    const slideAmount = 200;
    const minOffset = -800; // Límite máximo de desplazamiento (ajustar según necesidad)
    const newOffset = Math.max(tabSliderOffset - slideAmount, minOffset); // Deslizar hacia la izquierda (mostrar tabs de la derecha)  
    setTabSliderOffset(newOffset);
  };

  // Soporte táctil para el slider
  const [touchStart, setTouchStart] = useState(0);
  const [touchEnd, setTouchEnd] = useState(0);

  const handleTouchStart = (e) => {
    setTouchStart(e.touches[0].clientX);
  };

  const handleTouchMove = (e) => {
    setTouchEnd(e.touches[0].clientX);
  };

  const handleTouchEnd = () => {
    if (!touchStart || !touchEnd) return;
    const distance = touchStart - touchEnd;
    const isLeftSwipe = distance > 50;
    const isRightSwipe = distance < -50;

    if (isLeftSwipe && canSlideRight) slideTabsRight();
    if (isRightSwipe && canSlideLeft) slideTabsLeft();
  };

  // Actualizar estados de los botones slider
  useEffect(() => {
    setCanSlideLeft(tabSliderOffset < 0);
    setCanSlideRight(tabSliderOffset > -800); // Ajustar según el contenido real
  }, [tabSliderOffset]);

  const {
    register, handleSubmit, control, reset, formState:{ errors }, watch, setValue
  } = useForm({
    
    shouldUnregister: false, // ⬅ evita perder valores al ocultar tabs
    defaultValues: {
      // General
      skillname: '',
      skilldef: '',
      skilltype: 'OUT',
      idcampaign: '',
      iddatastructure: '',
      weight: 100,
      use_script: true,
      ring_tone: 'ninguno',
      forbid_redial_from_script: false,
      no_agenda_group: false,

      // Grabación
      record_calls: true,
      stop_record_on_transfer: false,
      copy_audio_enable: false,
      copy_audio_folder: '',
      copy_audio_format: 'wav',
      copy_audio_filename: '',
      copy_audio_by_final: false,
      copy_audio_min_seconds: 0,
      record_split_channels: true,
      record_ondemand_format: 'wav',
      record_storage_path: '/mnt/dialer-recordings/',
      record_mode: 'QUEUE',
      vol_tx: 50,
      vol_rx: 50,

      // Script
      script_name: '',
      finales_name: '',
      agenda_skilldef: '',
      id_crm: '',
      hide_codebar: false,
      hide_script: false,
      hide_title_info: false,
      script_maxtime_sec: null,
      script_maxtime_enable: false,
      script_auto_final_code: '',
      script_auto_final_desc: '',
      new_calls_mode: 'BUSCAR,INSERTAR',
      insertion_load_code: null,

      // Llamadas salientes
      dialing_mode: 'Manual',
      overdialing_mode: '', // Vacío por defecto, se establece según dialing_mode
      overdialing: false,
      alt_contacts: false,
      engineai_endpoint: '',
      engineai_sip_server: '',
      engineai_sip_port: 5060,
      engineai_extension: '',
      engineai_password: '',
      engineai_prefix: '',
      engineai_agent_id: '',
      engineai_max_concurrent: 5,
      callerid: '',
      forceani: '',
      order_mode: 'Prioridad',
      load_selection: 'Aleatoria',
      calls_per_agent: null,
      readywait: null,
      factor: 1.2,
      factor_increment: 0.1,
      analysis_interval_min: 5,
      factor_adjustment: 0.1,
      route_type: 'Trunk',
      route_value: '',
      route_format: 'Prefijo',
      route_format_value: '',
      destination_type: 'DialPlan',
      id_dialplan: null,
      dialplan_name: '',
      max_ring_seconds: 25,
      max_calls_per_sec: 10,

      // AMD
      amd_enable: false,
      amd_treat_notsure_as_machine: false,
      amd_profile: '(Default)',

      // ANI / límites
      force_ani_enable: false,
      ani_to_show: '',
      max_calls_skill: 0,
      min_free_agents: 0,

      // Locuciones
      language: 'default',
      moh_queue: 'default',
      moh_hold: 'default',

      // Numeración permitida
      allowed_default_action: 'Accept',
      allowed_rules: [],

      // Hijos
      rules: [{ rule_type: 'MaximoIntentos', retry_value: 15, priority: 0, events: 0, enabled: true }],
      schedule: [],
      overflow: [],
      robinson: { list_name:'', use_api:false, api_url:'', api_timeout_ms:null }
    }
  });

  // dependencias de controles (después de useForm)
  const skillType = watch('skilltype');
  const dialingMode = watch('dialing_mode');
  const overdialingMode = watch('overdialing_mode');
  const useScript = watch('use_script');
  const recordCalls = watch('record_calls');
  const recordStoragePathValue = watch('record_storage_path');
  const destinationType = watch('destination_type');
  const routeType = watch('route_type');
  const copyAudioEnable = watch('copy_audio_enable');
  const amdEnable = watch('amd_enable');
  const forceAniEnable = watch('force_ani_enable');
  const scriptMaxtimeEnable = watch('script_maxtime_enable');
  // engineAiEnabled eliminado - se controla automáticamente por dialingMode
  const ivrConditionsWatch = watch('ivr_conditions') || [];
  const hasIvrExtensionCondition = ivrConditionsWatch.some(condition => condition?.type === 'extension');
  
  const allowedDefault = watch('allowed_default_action') ?? 'Accept';
  const skillName = watch('skillname');
  const watchMatchMode = watch('inbound_match_mode');
  const [didInputValue, setDidInputValue] = useState('');
  const matchModeRef = React.useRef(null);
  useEffect(() => {
    if (matchModeRef.current !== null && watchMatchMode !== matchModeRef.current) {
      setValue('inbound_did', '');
      setDidInputValue('');
    }
    matchModeRef.current = watchMatchMode;
  }, [watchMatchMode, setValue]);
  // Reset ref al abrir el dialog para no borrar al cargar
  useEffect(() => {
    matchModeRef.current = null;
  }, [open]);
  const lockedDataStructureId = editingSkill?.iddatastructure ?? null;
  const isDataStructureLocked = lockedDataStructureId !== null && lockedDataStructureId !== undefined && lockedDataStructureId !== '';

  // Auto-configuración para EngineAI no necesaria, se maneja por dialing_mode

  // Función para generar skilldef automáticamente
  const generateSkillDef = (name) => {
    if (!name) return '';
    return name
      .toLowerCase()
      .normalize('NFD')
      .replace(/[\u0300-\u036f]/g, '') // Remover tildes y diacríticos
      .replace(/ñ/g, 'n')
      .replace(/[^a-z0-9\s]/g, '') // Remover caracteres especiales excepto espacios
      .trim()
      .replace(/\s+/g, ''); // Remover espacios (sin guiones bajos)
  };

  // Efecto para actualizar skilldef cuando cambie skillname (solo en modo creación)
  React.useEffect(() => {
    if (!editingSkill && skillName) {
      setValue('skilldef', generateSkillDef(skillName));
    }
  }, [skillName, editingSkill, setValue]);

  // field arrays
  const { fields: ruleFields, append: ruleAppend, remove: ruleRemove } = useFieldArray({ control, name: 'rules' });
  const { fields: schedFields, append: schedAppend, remove: schedRemove } = useFieldArray({ control, name: 'schedule' });
  const { fields: ofFields, append: ofAppend, remove: ofRemove } = useFieldArray({ control, name: 'overflow' });
  const { fields: allowFields, append: allowAppend, remove: allowRemove, move: allowMove } = useFieldArray({ control, name: 'allowed_rules' });

  // cargar catálogos y lista
  // Load skill statuses
  const loadSkillStatuses = async (skillList) => {
    setStatusesLoadingAll(true);
    try {
      // Consultar todos los engines en una sola llamada
      const response = await apiCall('/dialer/engines');
      const engines = Array.isArray(response?.data) ? response.data : [];
      const statusMap = {};

      engines.forEach(engine => {
        const def = engine.skillDef || engine.skilldef;
        if (def) {
          statusMap[def] = !!engine.isRunning;
        }
      });

      // Completar con false para skills sin engine activo
      (skillList || []).forEach(skill => {
        if (statusMap[skill.skilldef] === undefined) {
          statusMap[skill.skilldef] = false;
        }
      });

      setSkillStatuses(statusMap);
    } catch (error) {
      console.error('Error loading skill statuses:', error);
    } finally {
      setStatusesLoadingAll(false);
    }
  };

  // Toggle skill start/stop
  const toggleSkillStatus = async (skill) => {
    const skilldef = skill.skilldef;
    const isCurrentlyRunning = skillStatuses[skilldef];
    const action = isCurrentlyRunning ? 'stop' : 'start';

    try {
      setStatusLoading(prev => ({ ...prev, [skilldef]: true }));

      const response = await apiCall(`/dialer/skills/${skilldef}/${action}`, {
        method: 'POST'
      });

      if (response.success) {
        // Update the local status
        setSkillStatuses(prev => ({
          ...prev,
          [skilldef]: !isCurrentlyRunning
        }));
        // Actualizar columna de estado (BD) de forma optimista para la fila en pantalla
        setList(prev =>
          prev.map(item =>
            item.skilldef === skilldef
              ? { ...item, status: !isCurrentlyRunning ? 1 : 0 }
              : item
          )
        );
        show(`Skill ${skilldef} ${action === 'start' ? 'iniciado' : 'detenido'} correctamente`, 'success');
      } else {
        show(`Error al ${action === 'start' ? 'iniciar' : 'detener'} el skill ${skilldef}`, 'error');
      }
    } catch (error) {
      console.error(`Error toggling skill ${skilldef}:`, error);
      show(`Error al ${action === 'start' ? 'iniciar' : 'detener'} el skill ${skilldef}`, 'error');
    } finally {
      setStatusLoading(prev => ({ ...prev, [skilldef]: false }));
    }
  };

  const refreshCrmConfigs = async (prefetchedResponse = null) => {
    if (!crmConfigsApi?.list && !prefetchedResponse) return [];
    try {
      const response = prefetchedResponse || await crmConfigsApi.list();
      const configs = response?.data ?? [];
      setCrmConfigList(configs);
      return configs;
    } catch (error) {
      console.error('[SKILLS] Error cargando CRM configs:', error);
      show('Error al cargar configuraciones de CRM', 'error');
      return [];
    }
  };

  const resetCrmForm = () => setCrmForm(EMPTY_CRM_FORM);

  const populateCrmForm = (config) => {
    if (!config) {
      resetCrmForm();
      return;
    }
    setCrmForm({
      name: config.name || '',
      path_api: config.path_api || '',
      path_index: config.path_index || '',
      path_sales: config.path_sales || ''
    });
  };

  const handleOpenCrmDialog = () => {
    const currentCrm = watch('id_crm');
    if (currentCrm) {
      setCrmEditingId(String(currentCrm));
      const existing = crmConfigList.find(cfg => String(cfg.id) === String(currentCrm));
      populateCrmForm(existing);
    } else {
      setCrmEditingId('');
      resetCrmForm();
    }
    setCrmDialogOpen(true);
  };

  const handleCrmSelectChange = (event) => {
    const value = event.target.value;
    setCrmEditingId(value);
    if (value) {
      const existing = crmConfigList.find(cfg => String(cfg.id) === String(value));
      populateCrmForm(existing);
    } else {
      resetCrmForm();
    }
  };

  const handleCrmInputChange = (field) => (event) => {
    const value = event.target.value;
    setCrmForm((prev) => ({ ...prev, [field]: value }));
  };

  const handleSaveCrm = async () => {
    if (savingCrm) return;

    const name = crmForm.name.trim();
    const pathApi = crmForm.path_api.trim();
    const pathIndex = crmForm.path_index.trim();
    const pathSales = crmForm.path_sales.trim();

    if (!name || !pathApi || !pathIndex || !pathSales) {
      show('Todos los campos de CRM son requeridos', 'error');
      return;
    }

    try {
      setSavingCrm(true);
      const payload = {
        name,
        path_api: pathApi,
        path_index: pathIndex,
        path_sales: pathSales,
      };
      let response;
      if (crmEditingId) {
        response = await crmConfigsApi.update(crmEditingId, payload);
        show('Configuración CRM actualizada');
      } else {
        response = await crmConfigsApi.create(payload);
        show('Configuración CRM creada correctamente');
      }
      setCrmDialogOpen(false);
      const configs = await refreshCrmConfigs();
      const rawId = crmEditingId || (response?.data?.id ?? response?.data?.id_crm);
      if (rawId) {
        setValue('id_crm', String(rawId));
      } else if (Array.isArray(configs)) {
        const match = configs.find((cfg) => cfg.name === payload.name);
        if (match?.id) {
          setValue('id_crm', String(match.id));
        }
      }
      setCrmEditingId('');
      resetCrmForm();
    } catch (error) {
      console.error('[SKILLS] Error creando CRM:', error);
      show(error?.message || 'No se pudo registrar el CRM', 'error');
    } finally {
      setSavingCrm(false);
    }
  };

  const loadAll = async () => {
    try {
      setLoading(true);
      const crmPromise = crmConfigsApi?.list ? crmConfigsApi.list() : Promise.resolve({ data: [] });
      const [sk, cm, ds, dp, amd, trunks, endGroups, crmConfigsResponse] = await Promise.all([
        skills.getAll(),
        campaigns.getAll(),
        datastructures.getAll(),
        apiCall('/dialplans'),
        apiCall('/amd'),
        apiCall('/trunks'),
        ends.getGroups({ include_inactive: true }),
        crmPromise
      ]);
      const skillList = sk?.data?.skills ?? [];
      setList(skillList);
      setCampList(cm?.data?.campaigns ?? []);
      setDsList(ds?.data?.items ?? []);
      setDialplanList(dp ?? []);
      setAmdList(amd ?? []);
      setTrunkList(trunks ?? []);
      setEndGroupsList(endGroups?.data ?? []);
      if (endGroups?.data?.length) {
        loadFinalOptions(endGroups.data);
      } else {
        setAllFinalOptions([]);
      }
      await refreshCrmConfigs(crmConfigsResponse);
      console.log('Dialplans loaded:', dp?.length, dp?.slice(0, 3));
      console.log('AMD loaded:', amd?.length, amd?.slice(0, 3));
      console.log('Trunks loaded:', trunks?.length, trunks?.slice(0, 3));
      console.log('End Groups loaded:', endGroups?.data?.length, endGroups?.data?.slice(0, 3));

      // Load skill statuses after setting the skill list
      if (skillList.length > 0) {
        await loadSkillStatuses(skillList);
      }
    } catch (e) {
      console.error(e); show('Error al cargar datos', 'error');
    } finally { setLoading(false); }
  };
  useEffect(()=>{ loadAll(); }, []);

  // Efectos para manejar dependencias automáticas
  useEffect(() => {
    // Solo establecer valores por defecto cuando NO estamos editando un skill existente
    // (evitar sobrescribir valores durante la carga inicial del formulario)
    if (editingSkill) return;

    // Limpiar modo de sobremarcación cuando cambia el tipo de marcación
    if (dialingMode === 'Manual' || dialingMode === 'Preview') {
      setValue('overdialing_mode', '');
      setValue('overdialing', false);
    }
    // Establecer valores por defecto según el tipo de marcación
    else if (dialingMode === 'Progresivo') {
      setValue('overdialing_mode', 'Transferencia directa');
    }
    else if (dialingMode === 'Predictivo') {
      setValue('overdialing_mode', 'Auto');
    }
    else if (dialingMode === 'IVR') {
      setValue('overdialing_mode', 'Blaster');
    }
  }, [dialingMode, setValue, editingSkill]);

  useEffect(() => {
    // Si el tipo de skill es IN, deshabilitar tabs de salientes
    if (skillType === 'IN') {
      setValue('dialing_mode', '');
      setValue('overdialing_mode', '');
      setValue('route_value', '');
      setValue('amd_enable', false);
    }
  }, [skillType, setValue]);

  useEffect(() => {
    // Si desactiva el script, limpiar campos relacionados
    if (!useScript) {
      setValue('script_name', '');
      setValue('finales_name', '');
      setValue('agenda_skilldef', '');
      setValue('script_maxtime_enable', false);
      setValue('script_maxtime_sec', null);
    }
  }, [useScript, setValue]);

  // Helper function to handle null/undefined values for select fields
  const safeSelectValue = (value, defaultValue = '') => {
    return (value !== null && value !== undefined && value !== '') ? String(value) : defaultValue;
  };

  // Reset form when editingSkill changes (to fix MUI Select timing issues)
  useEffect(() => {
    if (editingSkill && open) {
      console.log('=== RESET DEBUG ===');
      console.log('About to reset with editingSkill:', editingSkill);
      
      // Use setTimeout to ensure MUI components are fully rendered first
      setTimeout(() => {
        console.log('Setting individual field values...');
        
        // Set each field individually using setValue
        setValue('skillname', editingSkill.skillname || '');
        setValue('skilldef', editingSkill.skilldef || '');
        setValue('skilltype', safeSelectValue(editingSkill.skilltype, 'OUT'));
        setValue('idcampaign', safeSelectValue(editingSkill.idcampaign, ''));
        setValue('iddatastructure', safeSelectValue(editingSkill.iddatastructure, ''));
        setValue('weight', editingSkill.weight || 100);
        setValue('use_script', !!editingSkill.use_script);
        setValue('ring_tone', safeSelectValue(editingSkill.ring_tone, 'ninguno'));
        setValue('forbid_redial_from_script', !!editingSkill.forbid_redial_from_script);
        setValue('no_agenda_group', !!editingSkill.no_agenda_group);

        // Grabación
        setValue('record_calls', !!editingSkill.record_calls);
        setValue('stop_record_on_transfer', !!editingSkill.stop_record_on_transfer);
        setValue('copy_audio_enable', !!editingSkill.copy_audio_enable);
        setValue('copy_audio_folder', editingSkill.copy_audio_folder || '');
        setValue('copy_audio_format', safeSelectValue(editingSkill.copy_audio_format, 'wav'));
        setValue('copy_audio_filename', editingSkill.copy_audio_filename || '');
        setValue('copy_audio_by_final', !!editingSkill.copy_audio_by_final);
        setValue('copy_audio_min_seconds', editingSkill.copy_audio_min_seconds || 0);
        setValue('record_split_channels', !!editingSkill.record_split_channels);
        setValue('record_ondemand_format', safeSelectValue(editingSkill.record_ondemand_format, 'wav'));
        setValue('record_storage_path', editingSkill.record_storage_path || '/mnt/dialer-recordings/');
        setValue('record_mode', safeSelectValue(editingSkill.record_mode, 'QUEUE') || 'QUEUE');
        setValue('vol_tx', editingSkill.vol_tx || 50);
        setValue('vol_rx', editingSkill.vol_rx || 50);

        // Script
        setValue('id_crm', editingSkill.id_crm ? String(editingSkill.id_crm) : '');
        // Set script fields later to avoid conflicts with useEffect cleanup
        setTimeout(() => {
          if (editingSkill?.use_script) {
            setValue('script_name', editingSkill.script_name || '');
            setValue('finales_name', editingSkill.finales_name || '');
            setValue('agenda_skilldef', editingSkill.agenda_skilldef || '');
          }
        }, 50);
        setValue('hide_codebar', !!editingSkill.hide_codebar);
        setValue('hide_script', !!editingSkill.hide_script);
        setValue('hide_title_info', !!editingSkill.hide_title_info);
        setValue('script_maxtime_sec', editingSkill.script_maxtime_sec || null);
        setValue('script_maxtime_enable', !!(editingSkill.script_maxtime_sec));
        setValue('script_auto_final_code', editingSkill.script_auto_final_code || '');
        setValue('script_auto_final_desc', editingSkill.script_auto_final_desc || '');
        setValue('new_calls_mode', safeSelectValue(editingSkill.new_calls_mode, 'BUSCAR,INSERTAR'));
        setValue('insertion_load_code', editingSkill.insertion_load_code || null);

        // Llamadas salientes  
        setValue('dialing_mode', safeSelectValue(editingSkill.dialing_mode, 'Manual'));
        setValue('overdialing_mode', safeSelectValue(editingSkill.overdialing_mode, ''));
        setValue('overdialing', !!editingSkill.overdialing);
        setValue('alt_contacts', !!editingSkill.alt_contacts);
        setValue('callerid', editingSkill.callerid || '');
        setValue('forceani', editingSkill.forceani || '');
        setValue('order_mode', safeSelectValue(editingSkill.order_mode, 'Prioridad'));
        setValue('load_selection', safeSelectValue(editingSkill.load_selection, 'Aleatoria'));
        setValue('calls_per_agent', editingSkill.calls_per_agent || null);
        setValue('readywait', editingSkill.readywait || null);
        setValue('factor', editingSkill.factor || 1.2);
        setValue('factor_increment', editingSkill.factor_increment || 0.1);
        setValue('analysis_interval_min', editingSkill.analysis_interval_min || 5);
        setValue('factor_adjustment', editingSkill.factor_adjustment || 0.1);
        setValue('route_type', safeSelectValue(editingSkill.route_type, 'Trunk'));
        setValue('route_value', editingSkill.route_value || '');
        setValue('route_format', safeSelectValue(editingSkill.route_format, 'Prefijo'));
        setValue('route_format_value', editingSkill.route_format_value || '');
        setValue('destination_type', editingSkill.destination_type || '');
        setValue('id_dialplan', editingSkill.id_dialplan || null);
        setValue('dialplan_name', editingSkill.dialplan_name || '');
        setValue('max_ring_seconds', editingSkill.max_ring_seconds || 25);
        setValue('max_calls_per_sec', editingSkill.max_calls_per_sec || 10);
        let engineAiConfig = editingSkill.extra_config?.engineai || editingSkill.extra_config?.eleven;
        if (!engineAiConfig && typeof editingSkill.extra_config === 'string') {
          try {
            const parsed = JSON.parse(editingSkill.extra_config);
            engineAiConfig = parsed?.engineai || parsed?.eleven;
          } catch {
            engineAiConfig = undefined;
          }
        }
        // Solo campos necesarios para EngineAI
        setValue('engineai_endpoint', engineAiConfig?.endpoint || '');
        setValue('engineai_sip_server', engineAiConfig?.sipServer || '');
        setValue('engineai_sip_port', engineAiConfig?.sipPort || 5060);
        setValue('engineai_extension', engineAiConfig?.extension || '');
        setValue('engineai_password', engineAiConfig?.password || '');
        setValue('engineai_prefix', engineAiConfig?.prefix || '');
        setValue('engineai_agent_id', engineAiConfig?.agentId || '');
        setValue('engineai_max_concurrent', engineAiConfig?.maxConcurrent || 5);

        // AMD
        setValue('amd_enable', !!editingSkill.amd_enable);
        setValue('amd_treat_notsure_as_machine', !!editingSkill.amd_treat_notsure_as_machine);
        setValue('amd_profile', safeSelectValue(editingSkill.amd_profile, '(Default)'));

        // ANI / límites - Cargar desde columnas originales
        setValue('force_ani_enable', !!editingSkill.force_ani_enable);
        setValue('ani_to_show', editingSkill.ani_to_show || '');
        setValue('max_calls_skill', editingSkill.max_calls_skill || 0);
        setValue('min_free_agents', editingSkill.min_free_agents || 0);

        // Locuciones
        setValue('language', editingSkill.language || 'default');
        setValue('moh_queue', editingSkill.moh_queue || 'default');
        setValue('moh_hold', editingSkill.moh_hold || 'default');

        // Numeración permitida - Handle allowed_numbers_config
        try {
          if (typeof editingSkill.allowed_numbers_config === 'object' && editingSkill.allowed_numbers_config) {
            setValue('allowed_default_action', safeSelectValue(editingSkill.allowed_numbers_config.default_action, 'Allow'));
            const rules = editingSkill.allowed_numbers_config.rules || [];
            setValue('allowed_rules', rules.map(rule => ({
              condition: safeSelectValue(rule.condition, 'Longitud <'),
              value: rule.value || '',
              action: safeSelectValue(rule.action, 'Deny')
            })));
          } else if (typeof editingSkill.allowed_numbers_config === 'string') {
            const parsed = JSON.parse(editingSkill.allowed_numbers_config);
            setValue('allowed_default_action', safeSelectValue(parsed.default_action, 'Allow'));
            const rules = parsed.rules || [];
            setValue('allowed_rules', rules.map(rule => ({
              condition: safeSelectValue(rule.condition, 'Longitud <'),
              value: rule.value || '',
              action: safeSelectValue(rule.action, 'Deny')
            })));
          } else {
            setValue('allowed_default_action', 'Accept');
            setValue('allowed_rules', []);
          }
        } catch (e) {
          console.log('Error parsing allowed_numbers_config:', e);
          setValue('allowed_default_action', 'Accept');
          setValue('allowed_rules', []);
        }

        // Hijos - cargar datos reales de las tablas hijo
        console.log('Loading child tables:', {
          rules: editingSkill._rules,
          schedule: editingSkill._schedule,
          overflow: editingSkill._overflow,
          robinson: editingSkill._robinson
        });
        setValue('rules', editingSkill._rules || []);
        setValue('schedule', editingSkill._schedule || []);
        setValue('overflow', editingSkill._overflow || []);
        setValue('robinson', editingSkill._robinson || { list_name:'', use_api:false, api_url:'', api_timeout_ms:null });

    // Cargar DID desde extra_config para skills IN
    if (editingSkill.extra_config?.inbound_did) {
      setValue('inbound_did', editingSkill.extra_config.inbound_did);
    }
    if (editingSkill.extra_config?.inbound_match_mode) {
      setValue('inbound_match_mode', editingSkill.extra_config.inbound_match_mode);
    }

    // Cargar campos IVR desde extra_config
    if (editingSkill.extra_config?.ivr_config) {
      const ivrConfig = editingSkill.extra_config.ivr_config;
      setValue('ivr_timeout_digit', ivrConfig.timeout_digit || 1);
      setValue('ivr_audio_path', ivrConfig.audio_path || '');
          setValue('ivr_wait_time', ivrConfig.wait_time || 5);
          setValue('ivr_conditions', ivrConfig.conditions || []);
          setValue('ivr_no_digit_action', ivrConfig.default_actions?.no_digit?.action || 'hangup');
          setValue('ivr_no_digit_destination', ivrConfig.default_actions?.no_digit?.destination || '');
          setValue('ivr_timeout_action', ivrConfig.default_actions?.timeout?.action || 'hangup');
          setValue('ivr_timeout_destination', ivrConfig.default_actions?.timeout?.destination || '');
          setValue('ivr_require_agents', ivrConfig.require_agents !== false);
        }

        console.log('All field values set individually');
      }, 200); // 200ms delay to ensure MUI components are rendered and other useEffects have run
      
    }
  }, [editingSkill, open, setValue]);

  const openCreate = () => {
    setEditingSkill(null);
    setTabSliderOffset(0); // Reset slider position
    reset({
      // General
      skillname: '',
      skilldef: '',
      skilltype: 'OUT',
      idcampaign: '',
      iddatastructure: '',
      weight: 100,
      use_script: true,
      ring_tone: 'ninguno',
      forbid_redial_from_script: false,
      no_agenda_group: false,

      // Grabación
      record_calls: true,
      stop_record_on_transfer: false,
      copy_audio_enable: false,
      copy_audio_folder: '',
      copy_audio_format: 'wav',
      copy_audio_filename: '',
      copy_audio_by_final: false,
      copy_audio_min_seconds: 0,
      record_split_channels: true,
      record_ondemand_format: 'wav',
      record_storage_path: '/mnt/dialer-recordings/',
      record_mode: 'QUEUE',
      vol_tx: 50,
      vol_rx: 50,

      // Script
      script_name: '',
      finales_name: '',
      agenda_skilldef: '',
      id_crm: '',
      hide_codebar: false,
      hide_script: false,
      hide_title_info: false,
      script_maxtime_sec: null,
      script_maxtime_enable: false,
      script_auto_final_code: '',
      script_auto_final_desc: '',
      new_calls_mode: 'BUSCAR,INSERTAR',
      insertion_load_code: null,

      // Llamadas salientes
      dialing_mode: 'Manual',
      overdialing_mode: 'Sobremarcacion',
      overdialing: false,
      alt_contacts: false,
      callerid: '',
      forceani: '',
      order_mode: 'Prioridad',
      load_selection: 'Aleatoria',
      analysis_time_min: null,
      calls_per_agent: null,
      readywait: null,
      route_type: 'Trunk',
      route_value: '',
      route_format: 'Prefijo',
      route_format_value: '',
      destination_type: 'DialPlan',
      id_dialplan: null,
      dialplan_name: '',
      max_ring_seconds: 25,
      max_calls_per_sec: 10,

      // AMD
      amd_enable: false,
      amd_treat_notsure_as_machine: false,
      amd_profile: '(Default)',

      // ANI / límites
      force_ani_enable: false,
      ani_to_show: '',
      max_calls_skill: 0,
      min_free_agents: 0,

      // Locuciones
      language: 'default',
      moh_queue: 'default',
      moh_hold: 'default',

      // Numeración permitida
      allowed_default_action: 'Accept',
      allowed_rules: [],

      // IVR/Blaster
      ivr_require_agents: true,

      // Hijos
      rules: [{ rule_type: 'MaximoIntentos', retry_value: 15, priority: 0, events: 0, enabled: true }],
      schedule: [],
      overflow: [],
      robinson: { list_name:'', use_api:false, api_url:'', api_timeout_ms:null }
    });
    setTab(0);
    setOpen(true);
  };

  const openEdit = async (skill) => {
    if (skillStatuses[skill.skilldef]) {
      show('No se puede editar un skill iniciado. Detén el skill primero.', 'warning');
      return;
    }
    console.log('=== SKILL DEBUG ===');
    console.log('Full skill object:', skill);
    console.log('ring_tone value:', skill.ring_tone, 'type:', typeof skill.ring_tone);
    console.log('idcampaign value:', skill.idcampaign, 'type:', typeof skill.idcampaign);
    console.log('iddatastructure value:', skill.iddatastructure, 'type:', typeof skill.iddatastructure);
    console.log('dialing_mode value:', skill.dialing_mode, 'type:', typeof skill.dialing_mode);
    console.log('overdialing_mode value:', skill.overdialing_mode, 'type:', typeof skill.overdialing_mode);
    console.log('==================');
    
    try {
      // Fetch complete skill details with child tables
      const response = await skills.getById(skill.id);
      console.log('Complete skill data:', response);
      
      if (response?.data?.skill) {
        // Use the complete skill data instead of the list item
        const completeSkill = response.data.skill;
        const skillRules = response.data.rules || [];
        const skillSchedule = response.data.schedule || [];
        const skillLocuciones = response.data.locuciones || {};
        const skillOverflow = response.data.overflow || [];
        const skillRobinson = response.data.robinson || {};
        
        // Store complete data for form population
        completeSkill._rules = skillRules;
        completeSkill._schedule = skillSchedule;
        completeSkill._locuciones = skillLocuciones;
        completeSkill._overflow = skillOverflow;
        completeSkill._robinson = skillRobinson;
        
        setEditingSkill(completeSkill);
      } else {
        // Fallback to list data if detail call fails
        setEditingSkill(skill);
      }
    } catch (error) {
      console.error('Error loading skill details:', error);
      // Fallback to list data if detail call fails
      setEditingSkill(skill);
    }
    
    setTabSliderOffset(0); // Reset slider position
    setTab(0);
    setOpen(true);
  };

  const onSubmit = async (f) => {
    if (submitting) return; // Prevenir doble envío

    // Si hay texto pendiente en el input de DID, convertirlo en chip antes de guardar
    if (f.skilltype === 'IN' && didInputValue.trim() && f.inbound_match_mode !== 'trunk') {
      const pending = didInputValue.trim();
      const current = (f.inbound_did || '').split(',').map(v => v.trim()).filter(Boolean);
      if (!current.includes(pending)) {
        current.push(pending);
      }
      f.inbound_did = current.join(',');
      setValue('inbound_did', f.inbound_did);
      setDidInputValue('');
    }

    try {
      setSubmitting(true);
      // Validar skilldef único para nuevos skills
      if (!editingSkill && f.skilldef) {
        const existingSkill = list.find(skill => 
          skill.skilldef?.toLowerCase() === f.skilldef?.toLowerCase()
        );
        if (existingSkill) {
          show(`Ya existe un skill con la definición "${f.skilldef}"`, 'error');
          return;
        }
      }

      // Validaciones específicas basadas en el mapeo
      if (f.skilltype === 'OUT' && !f.route_value) {
        show('Skills de tipo OUT requieren especificar un trunk de salida', 'error');
        return;
      }

      // Permitir guardar con "Usar CRM" activo pero sin configuración CRM seleccionada (Sin CRM)
      // if (f.use_script && !f.id_crm) {
      //   show('Si activa "Usar CRM" debe especificar una configuración CRM', 'error');
      //   return;
      // }

      if ((f.dialing_mode === 'Progresivo' || f.dialing_mode === 'Predictivo') && !f.overdialing_mode) {
        show('Los modos Progresivo y Predictivo requieren especificar un modo de sobremarcación', 'error');
        return;
      }

      if (f.copy_audio_enable && !f.copy_audio_folder) {
        show('Si activa segunda grabación debe especificar una carpeta destino', 'error');
        return;
      }

      if (f.script_maxtime_enable && !f.script_maxtime_sec) {
        show('Si activa tiempo máximo de script debe especificar los segundos', 'error');
        return;
      }
      if (f.script_maxtime_enable && !f.script_auto_final_code) {
        show('Si activa tiempo máximo de script debe seleccionar un final automático', 'error');
        return;
      }

      const dataStructureId = isDataStructureLocked ? lockedDataStructureId : f.iddatastructure;
      if (!dataStructureId) {
        show('Debe seleccionar una estructura de datos', 'error');
        return;
      }
      if (isDataStructureLocked && String(f.iddatastructure) !== String(lockedDataStructureId)) {
        show('No es posible modificar la estructura de datos en un skill existente', 'error');
        return;
      }

      // Mapear nombres a IDs para relaciones
      const selectedAmd = amdList.find(amd => amd.amdName === f.amd_profile);
      const selectedTrunk = trunkList.find(trunk => trunk.nombre === f.route_value);
      const selectedDialplan = dialplanList.find(dp => dp.id === Number(f.id_dialplan));

      // Construir extra_config preservando configuraciones existentes
      let extraConfig = {};

      // Preservar configuraciones existentes del skill en edición
      if (editingSkill?.extra_config) {
        extraConfig = { ...editingSkill.extra_config };
      }

      // Agregar configuración IVR si es tipo IVR
      if (f.dialing_mode === 'IVR') {
        const hasExtensionInSubmit = (f.ivr_conditions || []).some(condition => condition?.type === 'extension');
        const noDigitActionConfig = hasExtensionInSubmit
          ? { action: 'hangup', destination: '' }
          : {
              action: f.ivr_no_digit_action || 'hangup',
              destination: f.ivr_no_digit_destination || ''
            };

        extraConfig.ivr_config = {
          timeout_digit: Math.min(4, Math.max(1, f.ivr_timeout_digit || 1)),
          audio_path: f.ivr_audio_path || '',
          wait_time: f.ivr_wait_time || 5,
          conditions: f.ivr_conditions || [],
          require_agents: f.ivr_require_agents !== false,
          default_actions: {
            no_digit: noDigitActionConfig,
            timeout: {
              action: f.ivr_timeout_action || 'hangup',
              destination: f.ivr_timeout_destination || ''
            }
          }
        };
      }


      // Guardar require_agents para Blaster (overdialing_mode) si no es IVR
      if (f.dialing_mode !== 'IVR' && f.overdialing_mode === 'Blaster') {
        extraConfig.ivr_config = {
          ...(extraConfig.ivr_config || {}),
          require_agents: f.ivr_require_agents !== false
        };
      }

      // Guardar DID y match mode para skills IN
      if (f.skilltype === 'IN') {
        extraConfig.inbound_did = f.inbound_did || '';
        extraConfig.inbound_match_mode = f.inbound_match_mode || 'exact';
      }

      const enableEngineAi = f.dialing_mode === 'EngineAI';
      if (enableEngineAi) {
        extraConfig.engineai = {
          endpoint: f.engineai_endpoint || '',
          sipServer: f.engineai_sip_server || '',
          sipPort: f.engineai_sip_port || 5060,
          extension: f.engineai_extension || '',
          password: f.engineai_password || '',
          prefix: f.engineai_prefix || '',
          agentId: f.engineai_agent_id || '',
          maxConcurrent: f.engineai_max_concurrent || null
        };
      } else {
        if (extraConfig.engineai) delete extraConfig.engineai;
        if (extraConfig.eleven) delete extraConfig.eleven;
      }


      // Crear payload excluyendo campos que van en extra_config
      const {
        // Campos IVR que van en extra_config
        ivr_timeout_digit,
        ivr_audio_path,
        ivr_wait_time,
        ivr_conditions,
        ivr_no_digit_action,
        ivr_no_digit_destination,
        ivr_timeout_action,
        ivr_timeout_destination,
        ivr_require_agents,
        // Campos inbound que van en extra_config
        inbound_did,
        inbound_match_mode,
        // El resto de campos (incluyendo ANI que ya tienen columnas propias)
        ...payloadData
      } = f;

      const payload = {
        ...payloadData,
        idcampaign: f.idcampaign ? Number(f.idcampaign) : undefined,
        iddatastructure: Number(dataStructureId),
        // Mapear IDs de relaciones
        id_amd: selectedAmd ? selectedAmd.id : null,
        id_trunk: (f.route_type === 'Trunk' && selectedTrunk) ? selectedTrunk.idtrunk : null,
        id_dialplan: f.id_dialplan ? Number(f.id_dialplan) : null,
        dialplan_name: selectedDialplan ? selectedDialplan.dialplan : null,
        id_crm: f.id_crm ? Number(f.id_crm) : null,
        // Incluir extra_config
        extra_config: extraConfig,
        // Limpiar campos que no aplican según el tipo
        ...(f.skilltype === 'IN' && {
          dialing_mode: null,
          overdialing_mode: null,
          overdialing: false,
          route_type: null,
          route_value: null,
          alt_contacts: false,
          amd_enable: false,
          analysis_time_min: null,
          calls_per_agent: null
        }),
        // Limpiar overdialing_mode para modos que no lo soportan
        ...(f.dialing_mode === 'Manual' || f.dialing_mode === 'Preview') && {
          overdialing_mode: null
        }
      };

      if (editingSkill) {
        // Actualizar skill existente
        await skills.update(editingSkill.id, payload);

        // Limpiar caché de configuración del skill en sessionStorage
        const skilldefToUpdate = editingSkill.skilldef || f.skilldef;
        if (skilldefToUpdate) {
          try {
            const cacheKey = 'cti_skill_config_cache';
            const cached = sessionStorage.getItem(cacheKey);
            if (cached) {
              const parsedCache = JSON.parse(cached);
              // Eliminar la configuración antigua del skill del caché
              if (parsedCache[skilldefToUpdate]) {
                delete parsedCache[skilldefToUpdate];
                sessionStorage.setItem(cacheKey, JSON.stringify(parsedCache));
                console.log('[SKILLS] Caché de configuración actualizado para skill:', skilldefToUpdate);
              }
            }
          } catch (err) {
            console.warn('[SKILLS] Error actualizando caché:', err);
          }
        }

        show('Skill actualizado correctamente');
      } else {
        // Crear nuevo skill
        await skills.create(payload);
        show('Skill creado correctamente');
      }

      setOpen(false);
      loadAll();
    } catch (e) {
      console.error(e);
      show(e?.message || `Error al ${editingSkill ? 'actualizar' : 'crear'} skill`, 'error');
    } finally {
      setSubmitting(false);
    }
  };

  const onDelete = async (row) => {
    if (!window.confirm(`¿Eliminar el skill "${row.skillname}" y sus tablas?`)) return;
    if (deleting) return; // Prevenir múltiples eliminaciones

    try {
      setDeleting(true);
      await skills.delete(row.id);
      show('Skill eliminado');
      loadAll();
    } catch (e) {
      console.error(e); show('Error al eliminar', 'error');
    } finally {
      setDeleting(false);
    }
  };

  return (
    <Box p={2}>
      <Box display="flex" alignItems="center" justifyContent="space-between" flexWrap="wrap" gap={2} mb={2}>
        <Typography variant="h5" fontWeight={600}>Skills</Typography>
        <Box display="flex" alignItems="center" gap={2} flexWrap="wrap">
          <TextField
            select
            size="small"
            label="Filtrar por campaña"
            value={campaignFilter}
            onChange={(e) => setCampaignFilter(e.target.value)}
            sx={{ minWidth: 220 }}
            >
              <MenuItem value="">Todas</MenuItem>
              {campList.map(c => (
                <MenuItem key={c.idcampaign} value={String(c.idcampaign)}>
                  {c.descripcion}
                </MenuItem>
              ))}
          </TextField>
          <TextField
            select
            size="small"
            label="Estado"
            value={statusFilter}
            onChange={(e) => setStatusFilter(e.target.value)}
            sx={{ minWidth: 180 }}
          >
            <MenuItem value="all">Todos</MenuItem>
            <MenuItem value="active">Activos</MenuItem>
            <MenuItem value="inactive">Inactivos</MenuItem>
          </TextField>
          {canAction('create_skill') && <Button startIcon={<AddIcon />} variant="contained" onClick={openCreate}>Nuevo Skill</Button>}
        </Box>
      </Box>

      <Card sx={{ mb:2 }}>
        <CardContent>
          <Box display="flex" gap={2} alignItems="center">
            <SettingsIcon fontSize="small" />
            <Typography variant="body2">Total: {list.length}</Typography>
          </Box>
        </CardContent>
      </Card>

      <TableContainer component={Paper}>
        <Table size="small">
          <TableHead>
            <TableRow>
              {COLUMN_OPTIONS.map((column) => (
                <TableCell key={column.id} align={column.align || 'left'}>
                  <Stack spacing={0.5}>
                    <Stack direction="row" alignItems="center" spacing={0.5}>
                      {column.sortable ? (
                        <TableSortLabel
                          active={sortConfig.column === column.id}
                          direction={sortConfig.column === column.id ? sortConfig.direction : 'asc'}
                          onClick={() => handleSortChange(column.id)}
                        >
                          {column.label}
                        </TableSortLabel>
                      ) : (
                        <Typography variant="subtitle2">{column.label}</Typography>
                      )}
                      {column.filterable && (
                        <IconButton
                          size="small"
                          color={columnFilters[column.id] ? 'primary' : 'default'}
                          onClick={() =>
                            setActiveFilterColumn(prev => (prev === column.id ? null : column.id))
                          }
                        >
                          <FilterListIcon fontSize="small" />
                        </IconButton>
                      )}
                    </Stack>
                    {column.filterable && activeFilterColumn === column.id && (
                      <TextField
                        value={columnFilters[column.id] || ''}
                        onChange={(event) => handleColumnFilterChange(column.id, event.target.value)}
                        size="small"
                        placeholder="Filtrar"
                        variant="standard"
                        autoFocus
                        onBlur={() => setActiveFilterColumn(null)}
                        onKeyDown={(event) => {
                          if (event.key === 'Escape') {
                            event.preventDefault();
                            setActiveFilterColumn(null);
                          }
                        }}
                      />
                    )}
                  </Stack>
                </TableCell>
              ))}
            </TableRow>
          </TableHead>
          <TableBody>
            {sortedList.map((row)=>(
              <TableRow key={row.id} hover>
                <TableCell>{row.id}</TableCell>
                <TableCell>{row.skillname}</TableCell>
                <TableCell><Chip size="small" label={row.skilldef} /></TableCell>
                <TableCell>{row.campaign_name || '-'}</TableCell>
                <TableCell>{row.datastructure_name || '-'}</TableCell>
                <TableCell>{row.dialing_mode || '-'}</TableCell>
                <TableCell>
                  <Chip
                    size="small"
                    color={row.status === 1 ? 'success' : row.status === 2 ? 'warning' : 'default'}
                    label={row.status === 1 ? 'Activo' : row.status === 2 ? 'Pausado' : 'Inactivo'}
                  />
                </TableCell>
                <TableCell align="right">
                  {canAction('start_stop_skill') && (
                  <Tooltip title={skillStatuses[row.skilldef] ? "Detener" : "Iniciar"}>
                    <IconButton
                      size="small"
                      color={skillStatuses[row.skilldef] ? "error" : "success"}
                      onClick={() => toggleSkillStatus(row)}
                      disabled={statusLoading[row.skilldef] || statusesLoadingAll}
                    >
                      {skillStatuses[row.skilldef] ? (
                        <StopIcon fontSize="small" />
                      ) : (
                        <PlayIcon fontSize="small" />
                      )}
                    </IconButton>
                  </Tooltip>
                  )}
                  {canAction('edit_skill') && (
                    <Tooltip title={skillStatuses[row.skilldef] ? "No editable mientras está iniciado" : "Editar"}>
                      <span>
                        <IconButton
                          size="small"
                          color="primary"
                          onClick={()=>openEdit(row)}
                          disabled={skillStatuses[row.skilldef] || statusesLoadingAll}
                        >
                          <EditIcon fontSize="small"/>
                        </IconButton>
                      </span>
                    </Tooltip>
                  )}
                  {canAction('delete_skill') && (
                    <Tooltip title="Eliminar"><IconButton size="small" color="secondary" onClick={()=>onDelete(row)} disabled={deleting || statusesLoadingAll}><DeleteIcon fontSize="small"/></IconButton></Tooltip>
                  )}
                </TableCell>
              </TableRow>
            ))}
            {sortedList.length===0 && !loading && (
              <TableRow><TableCell colSpan={7} align="center">No hay skills.</TableCell></TableRow>
            )}
          </TableBody>
        </Table>
      </TableContainer>

      {/* DIALOG CREAR/EDITAR */}
      <Dialog
        open={open}
        onClose={(event, reason) => {
          // Prevenir cierre al hacer clic fuera o presionar ESC
          if (reason === 'backdropClick' || reason === 'escapeKeyDown') {
            return;
          }
          setOpen(false);
          setEditingSkill(null);
        }}
        maxWidth="md"
        fullWidth
        onKeyDown={handleKeyDown}
      >
        <DialogTitle>{editingSkill ? `Editar Skill: ${editingSkill.skillname}` : 'Nuevo Skill'}</DialogTitle>
        <DialogContent dividers sx={{ pt:0 }}>
          {/* Tabs con navegación */}
          <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
            <Tooltip title={!canSlideLeft ? "No hay más pestañas a la izquierda" : "Deslizar pestañas hacia la izquierda"}>
              <span>
                <IconButton 
                  onClick={slideTabsLeft} 
                  disabled={!canSlideLeft}
                  size="small"
                  sx={{ mr: 1, color: !canSlideLeft ? 'text.disabled' : 'primary.main' }}
                >
                  <ChevronLeftIcon />
                </IconButton>
              </span>
            </Tooltip>
            
            <Box sx={{ 
              flexGrow: 1, 
              overflow: 'hidden',  // Ocultar contenido que se sale
              position: 'relative',
              cursor: 'grab',
              '&:active': { cursor: 'grabbing' }
            }}
            onTouchStart={handleTouchStart}
            onTouchMove={handleTouchMove} 
            onTouchEnd={handleTouchEnd}
            >
              <Box sx={{
                transform: `translateX(${tabSliderOffset}px)`,
                transition: 'transform 0.3s ease',
                display: 'flex',
                width: 'max-content', // Permitir que el ancho se expanda según el contenido
                userSelect: 'none' // Prevenir selección de texto durante el arrastre
              }}>
                <Tabs 
                  value={tab} 
                  onChange={(_,v)=>setTab(v)} 
                  variant="standard"
                  sx={{ 
                    minWidth: 'max-content',
                    '& .MuiTabs-flexContainer': {
                      gap: '8px' // Espaciado entre tabs
                    },
                    '& .MuiTab-root': {
                      minWidth: 'auto',
                      padding: '6px 16px',
                      fontSize: '0.875rem',
                      whiteSpace: 'nowrap' // Prevenir salto de línea en labels
                    }
                  }}
              >
              <Tab label="General" {...a11yProps(0)} />
              <Tab label="Grabación" {...a11yProps(1)} disabled={skillType === 'IN' && !useScript} />
              <Tab label="Llamadas Salientes" {...a11yProps(2)} disabled={skillType === 'IN'} />
              <Tab label="CRM" {...a11yProps(3)} disabled={!useScript} />
              <Tab label="Locuciones" {...a11yProps(4)} />
              <Tab icon={<RuleIcon />} iconPosition="start" label="Reglas" {...a11yProps(5)} disabled={skillType === 'IN'} />
              <Tab icon={<AccessTimeIcon />} iconPosition="start" label="Horario" {...a11yProps(6)} />
              <Tab label="Desbordes" {...a11yProps(7)} />
              <Tab label="Robinson" {...a11yProps(8)} />
              <Tab label="Números Permitidos" {...a11yProps(9)} disabled={skillType === 'IN'} />
                </Tabs>
              </Box>
            </Box>
            
            <Tooltip title={!canSlideRight ? "No hay más pestañas a la derecha" : "Deslizar pestañas hacia la derecha"}>
              <span>
                <IconButton 
                  onClick={slideTabsRight} 
                  disabled={!canSlideRight}
                  size="small"
                  sx={{ ml: 1, color: !canSlideRight ? 'text.disabled' : 'primary.main' }}
                >
                  <ChevronRightIcon />
                </IconButton>
              </span>
            </Tooltip>
          </Box>

          {/* === General === */}
          <Box sx={{ display: tab===0 ? 'block' : 'none' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <TextField label="Nombre" {...register('skillname', { required:'Requerido' })} error={!!errors.skillname} helperText={errors.skillname?.message} />
              <TextField
                label=""
                {...register('skilldef', {
                  required:'Requerido',
                  pattern:{ value:/^[a-zA-Z0-9_]+$/, message:'Solo letras, números y _' }
                })}
                error={!!errors.skilldef}
                helperText={editingSkill ? "No se puede editar en skills existentes" : "Se genera automáticamente basado en el nombre"}
                disabled={true}
                InputProps={{
                  style: { backgroundColor: '#f5f5f5' }
                }}
              />
              <TextField select label="Tipo" value={skillType || 'OUT'} {...register('skilltype')}>
                <MenuItem value="OUT">OUT</MenuItem>
                <MenuItem value="IN">IN</MenuItem>
              </TextField>
              <TextField select label="Tono de llamada" defaultValue={editingSkill ? safeSelectValue(editingSkill.ring_tone, 'ninguno') : 'ninguno'} {...register('ring_tone')}>
                {RING_TONES.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
              </TextField>
              {skillType === 'IN' && (
                <TextField
                  select
                  label="Modo de Match"
                  {...register('inbound_match_mode')}
                  value={watchMatchMode || 'exact'}
                >
                  <MenuItem value="exact">Exacto</MenuItem>
                  <MenuItem value="prefix">Prefijo</MenuItem>
                  <MenuItem value="suffix">Sufijo</MenuItem>
                  <MenuItem value="trunk">Troncal</MenuItem>
                </TextField>
              )}
              {skillType === 'IN' && (
                watchMatchMode === 'trunk' ? (
                  <TextField
                    select
                    label="Troncal Entrante"
                    {...register('inbound_did')}
                    value={watch('inbound_did') || ''}
                  >
                    {trunkList.map(trunk => (
                      <MenuItem key={trunk.idtrunk} value={trunk.nombre}>
                        {trunk.nombre}
                      </MenuItem>
                    ))}
                  </TextField>
                ) : (
                  <Controller
                    name="inbound_did"
                    control={control}
                    defaultValue={editingSkill?.extra_config?.inbound_did || ''}
                    render={({ field }) => {
                      const chips = field.value ? field.value.split(',').filter(Boolean).map(v => v.trim()) : [];
                      return (
                        <Autocomplete
                          multiple
                          freeSolo
                          options={[]}
                          value={chips}
                          inputValue={didInputValue}
                          onInputChange={(_, val, reason) => {
                            if (reason === 'reset') { setDidInputValue(''); return; }
                            if (val.includes(',')) {
                              const v = val.replace(',', '').trim();
                              if (v && !chips.includes(v)) {
                                field.onChange([...chips, v].join(','));
                              }
                              setDidInputValue('');
                            } else {
                              setDidInputValue(val);
                            }
                          }}
                          onChange={(_, newValue) => {
                            field.onChange(newValue.map(v => v.trim()).filter(Boolean).join(','));
                            setDidInputValue('');
                          }}
                          renderTags={(value, getTagProps) =>
                            value.map((option, index) => (
                              <Chip size="small" label={option} {...getTagProps({ index })} key={index} />
                            ))
                          }
                          renderInput={(params) => (
                            <TextField
                              {...params}
                              label={watchMatchMode === 'prefix' ? 'Prefijo' : watchMatchMode === 'suffix' ? 'Sufijo' : 'Numero Entrante (DID)'}
                            />
                          )}
                        />
                      );
                    }}
                  />
                )
              )}
              <TextField select label="Campaña" defaultValue={editingSkill ? safeSelectValue(editingSkill.idcampaign, '') : ''} {...register('idcampaign', { required:'Requerido' })} error={!!errors.idcampaign} helperText={errors.idcampaign?.message}>
                <MenuItem value="">(Sin campaña)</MenuItem>
                {campList.map(c => <MenuItem key={c.idcampaign} value={c.idcampaign}>{c.descripcion}</MenuItem>)}
              </TextField>
              <TextField
                select
                label="Estructura de datos"
                defaultValue={editingSkill ? safeSelectValue(editingSkill.iddatastructure, '') : ''}
                {...register('iddatastructure', { required:'Requerido' })}
                error={!isDataStructureLocked && !!errors.iddatastructure}
                helperText={
                  isDataStructureLocked
                    ? 'No se puede modificar la estructura en skills existentes'
                    : errors.iddatastructure?.message
                }
                disabled={isDataStructureLocked}
              >
                {dsList.map(d => <MenuItem key={d.id} value={d.id}>{d.nombre}</MenuItem>)}
              </TextField>
              <TextField type="number" label="Peso" {...register('weight', { valueAsNumber:true })} />
              <Box sx={{ gridColumn: '1 / span 2' }}>
                <FormControlLabel control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.use_script : true} {...register('use_script')} />} label="Usar CRM" />
              </Box>
            </Box>
          </Box>

          {/* === Grabación === */}
          <Box sx={{ display: tab===1 ? 'block' : 'none' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <Box sx={{ gridColumn: '1 / span 2' }}>
                <FormControlLabel 
                  control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.record_calls : true} {...register('record_calls')} />} 
                  label="Activar grabación" 
                />
                <FormControlLabel 
                  control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.stop_record_on_transfer : false} {...register('stop_record_on_transfer')} />} 
                  label="Detener grabación en transferencias" 
                />
                <FormControlLabel
                  control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.record_split_channels : true} {...register('record_split_channels')} />}
                  label="Grabación estéreo (canales separados)"
                />
              </Box>

              <TextField 
                select 
                label="Formato grabación bajo demanda" 
                defaultValue={editingSkill ? safeSelectValue(editingSkill.record_ondemand_format, 'wav') : 'wav'}
                {...register('record_ondemand_format')}
                disabled={!recordCalls}
              >
                {RECORD_FORMATS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
              </TextField>
              <TextField
                label="Ruta de almacenamiento de grabaciones"
                defaultValue={editingSkill ? (editingSkill.record_storage_path || '/mnt/dialer-recordings/') : '/mnt/dialer-recordings/'}
                {...register('record_storage_path')}
                disabled={!recordCalls}
                helperText="Directorio base donde se guardarán los audios de este skill."
                placeholder="/mnt/dialer-recordings/"
              />
              <TextField
                select
                label="Modo de grabación"
                defaultValue={editingSkill ? (editingSkill.record_mode || 'QUEUE') : 'QUEUE'}
                {...register('record_mode')}
                disabled={!recordCalls}
                helperText="Define qué parte de la llamada se capturará."
              >
                {RECORDING_MODE_OPTIONS.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ gridColumn: '1 / span 2', border:'1px solid #eee', borderRadius:1, p:2 }}>
                <FormControlLabel 
                  control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.copy_audio_enable : false} {...register('copy_audio_enable')} />} 
                  label="Segunda grabación (copia)" 
                />
                <Box sx={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 2, mt: 1 }}>
                  <TextField 
                    label="Carpeta destino" 
                    {...register('copy_audio_folder')}
                    disabled={!copyAudioEnable}
                  />
                  <TextField 
                    select
                    label="Formato" 
                    defaultValue={editingSkill ? safeSelectValue(editingSkill.copy_audio_format, 'wav') : 'wav'}
                    {...register('copy_audio_format')}
                    disabled={!copyAudioEnable}
                  >
                    {AUDIO_FORMATS.map(f => <MenuItem key={f} value={f}>{f}</MenuItem>)}
                  </TextField>
                  <TextField 
                    label="Patrón nombre archivo" 
                    {...register('copy_audio_filename')}
                    disabled={!copyAudioEnable}
                    placeholder="{skill}_{datetime}_{ani}"
                  />
                  <TextField 
                    type="number"
                    label="Duración mínima (seg)" 
                    {...register('copy_audio_min_seconds', { valueAsNumber:true })}
                    disabled={!copyAudioEnable}
                  />
                  <FormControlLabel 
                    control={<Checkbox {...register('copy_audio_by_final')} disabled={!copyAudioEnable} defaultChecked={!!(editingSkill?.copy_audio_by_final)} />} 
                    label="Copiar solo con final específico" 
                  />
                </Box>
              </Box>

            </Box>
          </Box>

          {/* === Llamadas Salientes === */}
          <Box sx={{ display: tab===2 ? 'block' : 'none' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <TextField select label="Tipo de Marcación" defaultValue={editingSkill ? safeSelectValue(editingSkill.dialing_mode, 'Manual') : 'Manual'} {...register('dialing_mode')}>
                {DIALING_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              
              {/* Modo de marcación dinámico basado en tipo */}
              {(dialingMode === 'Progresivo') && (
                <TextField select label="Modo Progresivo" defaultValue={editingSkill ? safeSelectValue(editingSkill.overdialing_mode, 'Transferencia directa') : 'Transferencia directa'} {...register('overdialing_mode')}>
                  {PROGRESSIVE_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              )}
              
              {(dialingMode === 'Predictivo') && (
                <TextField select label="Modo Predictivo" defaultValue={editingSkill ? safeSelectValue(editingSkill.overdialing_mode, 'Auto') : 'Auto'} {...register('overdialing_mode')}>
                  {PREDICTIVE_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              )}

              {(dialingMode === 'IVR') && (
                <TextField select label="Modo IVR" defaultValue={editingSkill ? safeSelectValue(editingSkill.overdialing_mode, 'Blaster') : 'Blaster'} {...register('overdialing_mode')}>
                  {IVR_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
                </TextField>
              )}

              {(dialingMode === 'Manual' || dialingMode === 'Preview') && (
                <Box /> /* Espacio vacío */
              )}
              <TextField select label="Orden" defaultValue={editingSkill ? safeSelectValue(editingSkill.order_mode, 'Prioridad') : 'Prioridad'} {...register('order_mode')}>
                {ORDER_MODES.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>
              <TextField select label="Selección de carga" defaultValue={editingSkill ? safeSelectValue(editingSkill.load_selection, 'Aleatoria') : 'Aleatoria'} {...register('load_selection')}>
                {LOAD_SELECTION.map(m => <MenuItem key={m} value={m}>{m}</MenuItem>)}
              </TextField>

              {/* Primera fila: Tiempo de análisis (Solo para modos Auto/Auto-Ultra) */}
              {(overdialingMode === 'Auto' || overdialingMode === 'Auto - Ultra') && (
                <TextField
                  type="number"
                  label="Tiempo de análisis (minutos)"
                  {...register('analysis_interval_min', { valueAsNumber: true })}
                  defaultValue={editingSkill?.analysis_interval_min || 5}
                  helperText="Intervalo para ajustar automáticamente el factor"
                />
              )}

              {/* Factor Base (Solo para modos Auto/Auto-Ultra) */}
              {(overdialingMode === 'Auto' || overdialingMode === 'Auto - Ultra') && (
                <Box>
                  <Typography gutterBottom>
                    Factor Base: {watch('factor') || 1.2}
                  </Typography>
                  <Controller
                    name="factor"
                    control={control}
                    defaultValue={editingSkill?.factor || 1.2}
                    render={({ field }) => (
                      <input
                        type="range"
                        min="1.0"
                        max="10.0"
                        step="0.1"
                        value={field.value}
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        style={{
                          width: '100%',
                          height: '8px',
                          borderRadius: '5px',
                          backgroundColor: '#ddd',
                          outline: 'none',
                          WebkitAppearance: 'none'
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* Factor de incremento (Solo para modos Auto/Auto-Ultra) */}
              {(overdialingMode === 'Auto' || overdialingMode === 'Auto - Ultra') && (
                <Box>
                  <Typography gutterBottom>
                    Factor de incremento: {watch('factor_increment') || 0.1}
                  </Typography>
                  <Controller
                    name="factor_increment"
                    control={control}
                    defaultValue={editingSkill?.factor_increment || 0.1}
                    render={({ field }) => (
                      <input
                        type="range"
                        min="0.1"
                        max="2.0"
                        step="0.1"
                        value={field.value}
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        style={{
                          width: '100%',
                          height: '8px',
                          borderRadius: '5px',
                          backgroundColor: '#ddd',
                          outline: 'none',
                          WebkitAppearance: 'none'
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* Ajuste automático (Solo para modos Auto/Auto-Ultra) */}
              {(overdialingMode === 'Auto' || overdialingMode === 'Auto - Ultra') && (
                <Box>
                  <Typography gutterBottom>
                    Ajuste automático: ±{watch('factor_adjustment') || 0.1}
                  </Typography>
                  <Controller
                    name="factor_adjustment"
                    control={control}
                    defaultValue={editingSkill?.factor_adjustment || 0.1}
                    render={({ field }) => (
                      <input
                        type="range"
                        min="0.05"
                        max="1.0"
                        step="0.05"
                        value={field.value}
                        onChange={(e) => field.onChange(parseFloat(e.target.value))}
                        style={{
                          width: '100%',
                          height: '8px',
                          borderRadius: '5px',
                          backgroundColor: '#ddd',
                          outline: 'none',
                          WebkitAppearance: 'none'
                        }}
                      />
                    )}
                  />
                </Box>
              )}

              {/* Contactos alternativos (Solo para modos Auto/Auto-Ultra) */}
              {(overdialingMode === 'Auto' || overdialingMode === 'Auto - Ultra') && (
                <FormControlLabel
                  control={<Checkbox {...register('alt_contacts')} defaultChecked={!!(editingSkill?.alt_contacts)} />}
                  label="Usar contactos alternativos"
                />
              )}

              {/* Tiempo en Ready - Solo para Preview y Progresivo */}
              {(dialingMode === 'Preview' || dialingMode === 'Progresivo') && (
                <TextField
                  type="number"
                  label="Tiempo en Ready (segundos)"
                  {...register('readywait', { valueAsNumber: true })}
                  defaultValue={editingSkill?.readywait || 0}
                />
              )}
              
              {(overdialingMode === 'Sobremarcacion') && (
                <TextField 
                  type="number" 
                  label="Llamadas por agente" 
                  {...register('calls_per_agent', { valueAsNumber:true })} 
                  placeholder="Llamadas simultáneas"
                />
              )}
              
              {(overdialingMode === 'Agentes Virtuales') && (
                <TextField
                  type="number"
                  label="Número de agentes virtuales"
                  {...register('calls_per_agent', { valueAsNumber:true })}
                  defaultValue={editingSkill?.calls_per_agent || ''}
                />
              )}

              {(dialingMode === 'EngineAI') && (
                <Box sx={{ gridColumn: '1 / span 2', border:'1px solid #eee', borderRadius:1, p:2 }}>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <TextField label="Endpoint backend" {...register('engineai_endpoint')} placeholder="http://localhost:8083" />
                    <TextField label="Agente EngineAI" {...register('engineai_agent_id')} />
                    <TextField label="SIP Server" {...register('engineai_sip_server')} />
                    <TextField type="number" label="SIP Puerto" {...register('engineai_sip_port', { valueAsNumber:true })} />
                    <TextField label="Extensión SIP" {...register('engineai_extension')} />
                    <TextField label="Password SIP" type="password" {...register('engineai_password')} />
                    <TextField label="Prefijo" {...register('engineai_prefix')} placeholder="9" />
                    <TextField type="number" label="Máx. llamadas simultáneas" {...register('engineai_max_concurrent', { valueAsNumber:true })} />
                  </Box>
                </Box>
              )}


              {(overdialingMode === 'Blaster' || overdialingMode === 'Blaster Auto' || dialingMode === 'IVR') && (
                <TextField
                  type="number"
                  label={overdialingMode === 'Blaster Auto' ? 'Llamadas por agente' : 'Llamadas simultáneas IVR'}
                  {...register('calls_per_agent', { valueAsNumber:true })}
                  defaultValue={editingSkill?.calls_per_agent || ''}
                  helperText={overdialingMode === 'Blaster Auto' ? 'Factor multiplicador × agentes disponibles' : 'Número de llamadas simultáneas para IVR'}
                />
              )}
              {(overdialingMode === 'Blaster' || dialingMode === 'IVR') && overdialingMode !== 'Blaster Auto' && (
                <FormControlLabel
                  control={<Checkbox checked={!!watch('ivr_require_agents')} {...register('ivr_require_agents')} />}
                  label="Requiere agentes disponibles"
                />
              )}

              {/* AMD Fields - Solo mostrar para Progresivo y Predictivo */}
              {(dialingMode !== 'Manual' && dialingMode !== 'Preview') && (
                <Box sx={{ gridColumn: '1 / span 2', border:'1px solid #eee', borderRadius:1, p:1 }}>
                  <FormControlLabel control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.amd_enable : false} {...register('amd_enable')} />} label="Detectar contestador" />
                  <FormControlLabel control={<Checkbox defaultChecked={editingSkill ? !!editingSkill.amd_treat_notsure_as_machine : false} {...register('amd_treat_notsure_as_machine')} />} label="Tratar NOT SURE como MACHINE" />
                  <TextField select size="small" sx={{ ml:2, minWidth: 220 }} label="AMD Config" defaultValue={editingSkill?.amd_profile || ''} {...register('amd_profile')}>
                    {amdList.map(amd => (
                      <MenuItem key={amd.id} value={amd.amdName}>
                        {amd.amdName}
                      </MenuItem>
                    ))}
                  </TextField>
                </Box>
              )}

              <TextField select label="Ruta (tipo)" defaultValue="Trunk" {...register('route_type')}>
                {ROUTE_TYPES.map(r => <MenuItem key={r} value={r}>{r}</MenuItem>)}
              </TextField>
              {(routeType === 'Trunk') ? (
                <TextField 
                  select 
                  label="Ruta (valor)" 
                  {...register('route_value')} 
                  defaultValue={editingSkill?.route_value || ''}
                >
                  {trunkList.map(trunk => (
                    <MenuItem key={trunk.idtrunk} value={trunk.nombre}>
                      {trunk.nombre}
                    </MenuItem>
                  ))}
                </TextField>
              ) : (
                <TextField label="Ruta (valor)" {...register('route_value')} defaultValue={editingSkill?.route_value || ''} placeholder="Nombre del dialplan" />
              )}


              <TextField select label="Formato" defaultValue={editingSkill ? safeSelectValue(editingSkill.route_format, 'Prefijo') : 'Prefijo'} {...register('route_format')}>
                <MenuItem value="Prefijo">Prefijo</MenuItem>
              </TextField>
              <TextField label="Valor prefijo/sufijo" {...register('route_format_value')} />

              <TextField type="number" label="Max timbrado (seg)" {...register('max_ring_seconds', { valueAsNumber:true })} />
              <TextField type="number" label="Máx llamadas por seg" inputProps={{ min: 1, max: 50 }} {...register('max_calls_per_sec', { valueAsNumber:true })} />
              {dialingMode !== 'IVR' && <TextField type="number" label="Max. llamadas Skill" {...register('max_calls_skill', { valueAsNumber:true })} />}
              {dialingMode !== 'IVR' && <TextField type="number" label="Número de Agentes Libres" {...register('min_free_agents', { valueAsNumber:true })} />}

              {/* Campos IVR */}
              {dialingMode === 'IVR' && (
                <TextField
                  type="number"
                  label="Timeout entre dígitos (seg)"
                  inputProps={{ min: 1, max: 4 }}
                  {...register('ivr_timeout_digit', { valueAsNumber: true, min: { value: 1, message: 'Mínimo 1' }, max: { value: 4, message: 'Máximo 4' } })}
                  defaultValue={editingSkill?.extra_config?.ivr_config?.timeout_digit || 1}
                  error={!!errors.ivr_timeout_digit}
                  helperText={errors.ivr_timeout_digit?.message || "Tiempo máximo entre dígitos (1-4 seg)"}
                />
              )}
              {dialingMode === 'IVR' && (
                <Controller
                  name="ivr_audio_path"
                  control={control}
                  defaultValue={editingSkill?.extra_config?.ivr_config?.audio_path || ''}
                  render={({ field }) => (
                    <AudioPathSelector
                      value={field.value}
                      onChange={field.onChange}
                      label="Ruta del audio"
                      placeholder="enigma/audio1"
                      helperText="Path del archivo de audio sin extensión"
                    />
                  )}
                />
              )}
              {dialingMode === 'IVR' && (
                <TextField
                  type="number"
                  label="Tiempo de espera (seg)"
                  {...register('ivr_wait_time', { valueAsNumber: true })}
                  defaultValue={editingSkill?.extra_config?.ivr_config?.wait_time || 5}
                  helperText="Tiempo para marcar"
                />
              )}

              {/* Condiciones de Marcado IVR */}
              {dialingMode === 'IVR' && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Condiciones de Marcado
                  </Typography>
                  <IVRConditions
                    control={control}
                    register={register}
                    watch={watch}
                    setValue={setValue}
                    editingSkill={editingSkill}
                    skillsList={list}
                    dialplanList={dialplanList}
                  />
                </Box>
              )}

              {/* Acciones por Defecto IVR */}
              {dialingMode === 'IVR' && (
                <Box sx={{ gridColumn: '1 / span 2' }}>
                  <Typography variant="subtitle2" sx={{ mb: 1, fontWeight: 'bold' }}>
                    Acciones por Defecto
                  </Typography>
                  <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
                    <IVRDefaultAction
                      label="Si no marca otro dígito"
                      actionField="ivr_no_digit_action"
                      destinationField="ivr_no_digit_destination"
                      register={register}
                      watch={watch}
                      setValue={setValue}
                      editingSkill={editingSkill}
                      skillsList={list}
                      dialplanList={dialplanList}
                      configKey="no_digit"
                      disabled={hasIvrExtensionCondition}
                      disabledReason="Deshabilitado porque existe una condicion tipo extension."
                    />
                    <IVRDefaultAction
                      label="Si no marca nada (timeout)"
                      actionField="ivr_timeout_action"
                      destinationField="ivr_timeout_destination"
                      register={register}
                      watch={watch}
                      setValue={setValue}
                      editingSkill={editingSkill}
                      skillsList={list}
                      dialplanList={dialplanList}
                      configKey="timeout"
                    />
                  </Box>
                </Box>
              )}

              {/* Forzado de ANI - AL FINAL del tab Llamadas Salientes */}
              <Box sx={{ mt: 2, border:'1px solid #eee', borderRadius:1, p:2 }}>
                <FormControlLabel control={<Checkbox {...register('force_ani_enable')} defaultChecked={!!(editingSkill?.force_ani_enable)} />} label="Usar Forzado de ANI" />
                {forceAniEnable && (
                  <TextField label="Mostrar ANI (uno por línea)" multiline minRows={4} fullWidth {...register('ani_to_show')} sx={{ mt: 1 }} />
                )}
              </Box>

            </Box>
          </Box>

          {/* === CRM === */}
          <Box sx={{ display: tab===3 ? 'block' : 'none' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2}>
              <TextField
                select
                label="Finales"
                {...register('finales_name')}
                defaultValue={editingSkill?.finales_name || ''}
                disabled={!useScript}
                helperText="Seleccione el grupo de tipificaciones"
              >
                <MenuItem value="">
                  <em>Sin grupo de tipificaciones</em>
                </MenuItem>
                {endGroupsList.map((group) => (
                  <MenuItem key={group.id} value={group.id}>
                    {group.name}
                  </MenuItem>
                ))}
              </TextField>

              <Box sx={{ gridColumn: '1 / span 2', display: 'flex', gap: 2, alignItems: 'center' }}>
                <TextField
                  select
                  fullWidth
                  label="Configuración CRM"
                  {...register('id_crm')}
                  defaultValue={editingSkill ? (editingSkill.id_crm ? String(editingSkill.id_crm) : '') : ''}
                  disabled={!useScript}
                  sx={{ flex: 1, minWidth: 0 }}
                >
                  <MenuItem value="">
                    <em>Sin CRM</em>
                  </MenuItem>
                  {crmConfigList.map((config) => (
                    <MenuItem key={config.id} value={String(config.id)}>
                      {config.name}
                    </MenuItem>
                  ))}
                </TextField>
                <Button
                  variant="outlined"
                  startIcon={<AddIcon />}
                  onClick={handleOpenCrmDialog}
                  disabled={!useScript}
                  sx={{
                    height: '56px',
                    minWidth: '140px',
                    alignSelf: 'stretch',
                    whiteSpace: 'nowrap'
                  }}
                >
                  Nuevo CRM
                </Button>
              </Box>

              <Box sx={{ gridColumn: '1 / span 2' }}>
                <FormControlLabel
                  control={<Checkbox {...register('script_maxtime_enable')} disabled={!useScript} defaultChecked={!!(editingSkill?.script_maxtime_sec)} />}
                  label="Tiempo máximo de script"
                />
              </Box>

              {scriptMaxtimeEnable && (
                <Box sx={{ gridColumn: '1 / span 2', border:'1px solid #eee', borderRadius:1, p:2 }}>
                  <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
                    <TextField 
                      type="number"
                      label="Tiempo máximo (seg)" 
                      {...register('script_maxtime_sec', { valueAsNumber:true })}
                    />
                    <Controller
                      name="script_auto_final_code"
                      control={control}
                      render={({ field }) => {
                        const selectedOption = allFinalOptions.find(opt => opt.code === field.value) || null;
                        return (
                          <Autocomplete
                            options={allFinalOptions}
                            loading={finalsLoading}
                            value={selectedOption}
                            onChange={(_, newValue) => {
                              field.onChange(newValue?.code || '');
                            }}
                            getOptionLabel={(option) => {
                              return option?.label || option?.code || '';
                            }}
                            isOptionEqualToValue={(option, value) => {
                              return option?.code === value?.code;
                            }}
                            renderOption={(props, option) => (
                              <li {...props} key={option.id || option.code}>
                                {option.label}
                              </li>
                            )}
                            renderInput={(params) => (
                              <TextField
                                {...params}
                                label="Código final automático"
                                placeholder="Seleccione un final"
                                helperText="Final de cualquier grupo; se aplicará automáticamente al vencer el tiempo"
                              />
                            )}
                          />
                        );
                      }}
                    />
                    <TextField 
                      label="Descripción final automático" 
                      {...register('script_auto_final_desc')}
                      placeholder="Tiempo excedido"
                    />
                  </Box>
                </Box>
              )}

            </Box>
          </Box>

          {/* === Locuciones === */}
          <Box sx={{ display: tab===4 ? 'block' : 'none' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
              <TextField label="Idioma" {...register('language')} />
              <TextField label="Música en Cola" {...register('moh_queue')} />
              <TextField label="Música en Espera" {...register('moh_hold')} />
            </Box>
          </Box>

          {/* === Reglas === */}
          <Box sx={{ display: tab===5 ? 'block' : 'none' }}>
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">Reglas de marcación</Typography>
                <Button size="small" onClick={()=>ruleAppend({ rule_type:'Abandono', retry_value:0, priority:0, events:0 })}>Agregar</Button>
              </Box>
              <Divider sx={{ mb:1 }} />
              {ruleFields.length===0 && <Typography variant="body2">No hay reglas.</Typography>}
              {ruleFields.map((field, idx)=>(
                <Grid container spacing={1} key={field.id} alignItems="center" sx={{ mb:1 }}>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Tipo" {...register(`rules.${idx}.rule_type`)} defaultValue={field.rule_type || ''}>
                      {RULE_TYPES.filter(t => {
                        // Always show current selected type
                        if (t === watch(`rules.${idx}.rule_type`)) return true;
                        // Filter out types already selected in other rules
                        const allRules = watch('rules') || [];
                        return !allRules.some((rule, ruleIdx) => ruleIdx !== idx && rule.rule_type === t);
                      }).map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField type="number" fullWidth label={watch(`rules.${idx}.rule_type`) === 'MaximoIntentos' ? "Cantidad" : "Minutos"} {...register(`rules.${idx}.retry_value`, { valueAsNumber:true })} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField type="number" fullWidth label="Prioridad" {...register(`rules.${idx}.priority`, { valueAsNumber:true })} />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    {watch(`rules.${idx}.rule_type`) !== 'MaximoIntentos' && (
                      <Button color="secondary" onClick={()=>ruleRemove(idx)}>Quitar</Button>
                    )}
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>

          {/* === Horario === */}
          <Box sx={{ display: tab===6 ? 'block' : 'none' }}>
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">Horario</Typography>
                <Button size="small" onClick={()=>schedAppend({
                  tz_name:'America/Bogota',
                  mon:1,tue:1,wed:1,thu:1,fri:1,sat:0,sun:0,
                  time_start:'07:00', time_end:'19:00'
                })}>Agregar</Button>
              </Box>
              <Divider sx={{ mb:1 }} />
              {schedFields.length===0 && <Typography variant="body2">No hay rangos.</Typography>}
              {schedFields.map((f, idx)=>(
                <Box key={f.id} sx={{ border:'1px solid #eee', borderRadius:1, p:1, mb:1 }}>
                  <Grid container spacing={1} alignItems="center">
                    <Grid item xs={12} md={3}>
                      <TextField fullWidth label="Zona horaria" {...register(`schedule.${idx}.tz_name`)} />
                    </Grid>
                    <Grid item xs={12} md={4}>
                      <Box display="flex" alignItems="center" gap={1} flexWrap="wrap">
                        {['mon','tue','wed','thu','fri','sat','sun'].map(d => (
                          <FormControlLabel key={d} control={
                            <Controller
                              name={`schedule.${idx}.${d}`}
                              control={control}
                              defaultValue={d!=='sat' && d!=='sun'}
                              render={({ field }) => (
                                <Checkbox checked={!!field.value} onChange={(e)=>field.onChange(e.target.checked)} />
                              )}
                            />
                          } label={d.toUpperCase()} />
                        ))}
                      </Box>
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField label="Inicio" type="time" fullWidth {...register(`schedule.${idx}.time_start`)} />
                    </Grid>
                    <Grid item xs={6} md={2}>
                      <TextField label="Fin" type="time" fullWidth {...register(`schedule.${idx}.time_end`)} />
                    </Grid>
                    <Grid item xs={12} md={1}>
                      <Button color="secondary" onClick={()=>schedRemove(idx)}>Quitar</Button>
                    </Grid>
                  </Grid>
                </Box>
              ))}
            </Box>
          </Box>

          {/* === Desbordes === */}
          <Box sx={{ display: tab===7 ? 'block' : 'none' }}>
            <Box>
              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">Desbordes</Typography>
                <Button size="small" onClick={()=>ofAppend({ cond_type:'TiempoMaxEspera', enabled:true, threshold_int:5, action_type:'Colgar' })}>Agregar</Button>
              </Box>
              <Divider sx={{ mb:1 }} />
              {ofFields.length===0 && <Typography variant="body2">No hay desbordes.</Typography>}
              {ofFields.map((f, idx)=>(
                <Grid container spacing={1} key={f.id} alignItems="center" sx={{ mb:1 }}>
                  <Grid item xs={12} md={3}>
                    <TextField select fullWidth label="Condición" {...register(`overflow.${idx}.cond_type`)} defaultValue={f.cond_type || ''}>
                      {OVERFLOW_CONDS.map(t => <MenuItem key={t} value={t}>{t}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <FormControlLabel control={<Checkbox {...register(`overflow.${idx}.enabled`)} defaultChecked={!!(f.enabled)} />} label="Activo" />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <TextField type="number" fullWidth label="Umbral" {...register(`overflow.${idx}.threshold_int`, { valueAsNumber:true })} />
                  </Grid>
                  <Grid item xs={12} md={3}>
                    <TextField fullWidth label="Acción" {...register(`overflow.${idx}.action_type`)} defaultValue={f.action_type || ''} placeholder="Colgar/Transferir/IVR..." />
                  </Grid>
                  <Grid item xs={12} md={2}>
                    <Button color="secondary" onClick={()=>ofRemove(idx)}>Quitar</Button>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>

          {/* === Robinson === */}
          <Box sx={{ display: tab===8 ? 'block' : 'none' }}>
            <Box display="grid" gridTemplateColumns="1fr 1fr 1fr" gap={2}>
              <TextField label="Lista" {...register('robinson.list_name')} />
              <FormControlLabel control={<Checkbox {...register('robinson.use_api')} defaultChecked={!!(editingSkill?._robinson?.use_api)} />} label="Usar API" />
              <TextField label="API URL" {...register('robinson.api_url')} />
              <TextField type="number" label="Timeout (ms)" {...register('robinson.api_timeout_ms', { valueAsNumber:true })} />
            </Box>
          </Box>

          {/* === Números Permitidos === */}
          <Box sx={{ display: tab===9 ? 'block' : 'none' }}>
            <Box>
              <Box display="grid" gridTemplateColumns="1fr 1fr" gap={2} mb={1}>
                <TextField select label="Acción por defecto" value={allowedDefault} {...register('allowed_default_action')}>
                  {ALLOWED_DEFAULT_ACTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                </TextField>
              </Box>

              <Divider sx={{ my:1 }} />

              <Box display="flex" justifyContent="space-between" mb={1}>
                <Typography variant="subtitle1">Reglas</Typography>
                <Button size="small" onClick={()=>allowAppend({ condition:'Longitud <', value:'10', action:'Deny' })}>Agregar</Button>
              </Box>

              {allowFields.length===0 && <Typography variant="body2">No hay reglas.</Typography>}

              {allowFields.map((f, idx)=>(
                <Grid container spacing={1} key={f.id} alignItems="center" sx={{ mb:1 }}>
                  <Grid item xs={12} md={4}>
                    <TextField select fullWidth label="Condición" {...register(`allowed_rules.${idx}.condition`)} defaultValue={f.condition || ''}>
                      {ALLOWED_CONDITIONS.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField fullWidth label="Valor" {...register(`allowed_rules.${idx}.value`)} />
                  </Grid>
                  <Grid item xs={6} md={3}>
                    <TextField select fullWidth label="Acción" {...register(`allowed_rules.${idx}.action`)} defaultValue={f.action || ''}>
                      {ALLOWED_ACTIONS.map(a => <MenuItem key={a} value={a}>{a}</MenuItem>)}
                    </TextField>
                  </Grid>
                  <Grid item xs={12} md={2} display="flex" gap={1}>
                    <Button size="small" disabled={idx===0} onClick={()=>allowMove(idx, idx-1)}>↑</Button>
                    <Button size="small" disabled={idx===allowFields.length-1} onClick={()=>allowMove(idx, idx+1)}>↓</Button>
                    <Button size="small" color="secondary" onClick={()=>allowRemove(idx)}>Quitar</Button>
                  </Grid>
                </Grid>
              ))}
            </Box>
          </Box>

        </DialogContent>

        <DialogActions sx={{ justifyContent: 'space-between', alignItems: 'center' }}>
          {/* Indicador de progreso a la izquierda */}
          <Box sx={{ display: 'flex', alignItems: 'center', flex: 1 }}>
            <Typography variant="caption" color="textSecondary" sx={{ mr: 2 }}>
              Pestaña {tab + 1} de {totalTabs}
            </Typography>
            <Box sx={{ width: 120 }}>
              <div style={{
                width: '100%',
                height: '4px',
                backgroundColor: '#e0e0e0',
                borderRadius: '2px',
                overflow: 'hidden'
              }}>
                <div style={{
                  width: `${((tab + 1) / totalTabs) * 100}%`,
                  height: '100%',
                  backgroundColor: '#667eea',
                  transition: 'width 0.3s ease'
                }} />
              </div>
            </Box>
          </Box>

          {/* Botones a la derecha */}
          <Box>
            <Button 
              onClick={()=>{setOpen(false); setEditingSkill(null);}} 
              sx={{ mr: 1 }}
              disabled={submitting}
            >
              Cancelar
            </Button>
            <Button
              variant="contained"
              onClick={handleSubmit(onSubmit)}
              disabled={submitting}
              startIcon={submitting ? <CircularProgress size={16} color="inherit" /> : null}
            >
              {submitting ? 'Procesando...' : (editingSkill ? 'Actualizar' : 'Crear')}
            </Button>
          </Box>
      </DialogActions>
    </Dialog>

      <Dialog
        open={crmDialogOpen}
        onClose={() => { if (!savingCrm) { setCrmDialogOpen(false); setCrmEditingId(''); resetCrmForm(); } }}
        maxWidth="sm"
        fullWidth
      >
        <DialogTitle>Nueva configuración CRM</DialogTitle>
        <DialogContent dividers>
          <Box display="grid" gridTemplateColumns="1fr" gap={2} mt={1}>
            <TextField
              select
              label="Seleccionar configuración"
              value={crmEditingId}
              onChange={handleCrmSelectChange}
            >
              <MenuItem value="">
                <em>Nuevo CRM</em>
              </MenuItem>
              {crmConfigList.map((config) => (
                <MenuItem key={config.id} value={String(config.id)}>
                  {config.name}
                </MenuItem>
              ))}
            </TextField>
            <TextField
              label="Nombre"
              value={crmForm.name}
              onChange={handleCrmInputChange('name')}
              autoFocus
            />
            <TextField
              label="Ruta API"
              value={crmForm.path_api}
              onChange={handleCrmInputChange('path_api')}
              placeholder="http://cxdintranet.local:51002"
            />
            <TextField
              label="Ruta principal"
              value={crmForm.path_index}
              onChange={handleCrmInputChange('path_index')}
              placeholder="http://cxdintranet.local/conex/"
            />
            <TextField
              label="Ruta venta"
              value={crmForm.path_sales}
              onChange={handleCrmInputChange('path_sales')}
              placeholder="http://cxdintranet.local/conex/management/mobile"
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setCrmDialogOpen(false)} disabled={savingCrm}>
            Cancelar
          </Button>
          <Button
            variant="contained"
            onClick={handleSaveCrm}
            disabled={savingCrm}
            startIcon={savingCrm ? <CircularProgress size={16} color="inherit" /> : null}
          >
            {savingCrm ? 'Guardando...' : (crmEditingId ? 'Actualizar' : 'Guardar')}
          </Button>
        </DialogActions>
      </Dialog>

      <Snackbar open={snack.open} autoHideDuration={4000} onClose={closeSnack}>
        <Alert onClose={closeSnack} severity={snack.severity} variant="filled">
          {snack.message}
        </Alert>
      </Snackbar>
    </Box>
  );
}

// ============================================
// COMPONENTES IVR
// ============================================

// Componente para manejar las condiciones IVR
function IVRConditions({ control, register, watch, setValue, editingSkill, skillsList, dialplanList }) {
  const amdEnabled = watch('amd_enable') || false;
  const { fields, append, remove } = useFieldArray({
    control,
    name: 'ivr_conditions'
  });

  const watchedConditions = watch('ivr_conditions') || [];

  // Inicializar condiciones desde editingSkill
  React.useEffect(() => {
    if (editingSkill?.extra_config?.ivr_config?.conditions && fields.length === 0) {
      const existingConditions = editingSkill.extra_config.ivr_config.conditions;
      existingConditions.forEach(condition => {
        append(condition);
      });
    }
  }, [editingSkill, fields.length, append]);

  const addCondition = () => {
    append({
      type: 'digito',
      value: '',
      action: 'hangup',
      destination: ''
    });
  };

  const removeCondition = (index) => {
    remove(index);
  };

  // Validar duplicados
  const getUsedDigits = () => {
    return watchedConditions
      .map((condition, originalIndex) => ({ condition, originalIndex }))
      .filter(({ condition }) => condition?.type === 'digito' && condition?.value)
      .map(({ condition, originalIndex }) => ({ digit: condition.value, index: originalIndex }));
  };

  const hasExtensionCondition = () => {
    return watchedConditions.some((condition, index) => condition?.type === 'extension');
  };

  const hasAMDCondition = () => {
    return watchedConditions.some((condition, index) => condition?.type === 'amd');
  };

  return (
    <Box>
      {fields.map((field, index) => (
        <IVRConditionItem
          key={field.id}
          field={field}
          index={index}
          register={register}
          watch={watch}
          setValue={setValue}
          onRemove={removeCondition}
          skillsList={skillsList}
          dialplanList={dialplanList}
          usedDigits={getUsedDigits()}
          hasExtension={hasExtensionCondition()}
          hasAMD={hasAMDCondition()}
          amdEnabled={amdEnabled}
        />
      ))}

      <Button
        variant="outlined"
        onClick={addCondition}
        startIcon={<AddIcon />}
        sx={{ mt: 2 }}
      >
        Agregar Condición
      </Button>
    </Box>
  );
}

// Componente para cada condición individual
function IVRConditionItem({ field, index, register, watch, setValue, onRemove, skillsList, dialplanList, usedDigits, hasExtension, hasAMD, amdEnabled }) {
  const conditionType = watch(`ivr_conditions.${index}.type`);
  const conditionAction = watch(`ivr_conditions.${index}.action`);
  const conditionValue = watch(`ivr_conditions.${index}.value`);

  // Manejar cambios cuando el tipo cambia
  React.useEffect(() => {
    if (conditionType === 'extension') {
      // Si el tipo es extension, la acción siempre es extension y el destino se limpia
      setValue(`ivr_conditions.${index}.value`, '');
      setValue(`ivr_conditions.${index}.action`, 'extension');
      setValue(`ivr_conditions.${index}.destination`, '');
    } else if (conditionType === 'amd') {
      setValue(`ivr_conditions.${index}.value`, '');
    }
  }, [conditionType, index, setValue]);

  const getTypeOptions = () => {
    const options = [
      { value: 'digito', label: 'Dígito' }
    ];

    // Solo permitir Extension si no hay otra
    if (!hasExtension || conditionType === 'extension') {
      options.push({ value: 'extension', label: 'Extensión' });
    }

    // Solo permitir AMD si no hay otra Y si AMD está habilitado
    if (amdEnabled && (!hasAMD || conditionType === 'amd')) {
      options.push({ value: 'amd', label: 'AMD (Detección contestadora)' });
    }

    return options;
  };

  const getDigitError = () => {
    if (conditionType === 'digito' && conditionValue) {
      const duplicate = usedDigits.find(item =>
        item.digit === conditionValue && item.index !== index
      );
      return duplicate ? `El dígito ${conditionValue} ya está en uso` : null;
    }
    return null;
  };

  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2, mb: 2, position: 'relative' }}>
      <IconButton
        onClick={() => onRemove(index)}
        sx={{ position: 'absolute', top: 8, right: 8 }}
        size="small"
      >
        <DeleteIcon fontSize="small" />
      </IconButton>

      <Box display="grid" gridTemplateColumns="1fr 1fr 1fr 1fr" gap={2} sx={{ pr: 5 }}>
        <TextField
          select
          label="Tipo"
          {...register(`ivr_conditions.${index}.type`)}
          defaultValue={field.type || "digito"}
          size="small"
        >
          {getTypeOptions().map(option => (
            <MenuItem key={option.value} value={option.value}>
              {option.label}
            </MenuItem>
          ))}
        </TextField>

        {conditionType === 'digito' && (
          <TextField
            label="Dígito"
            {...register(`ivr_conditions.${index}.value`)}
            placeholder="0-9, *, #"
            error={!!getDigitError()}
            helperText={getDigitError()}
            inputProps={{ maxLength: 1 }}
            size="small"
          />
        )}

        {(conditionType === 'extension' || conditionType === 'amd') && (
          <Box /> /* Espacio vacío */
        )}

        <IVRActionSelect
          register={register}
          actionField={`ivr_conditions.${index}.action`}
          destinationField={`ivr_conditions.${index}.destination`}
          watch={watch}
          skillsList={skillsList}
          defaultValue={field.action}
          disabled={conditionType === 'extension'}
        />

        <IVRDestinationField
          register={register}
          watch={watch}
          setValue={setValue}
          actionField={`ivr_conditions.${index}.action`}
          destinationField={`ivr_conditions.${index}.destination`}
          skillsList={skillsList}
          dialplanList={dialplanList}
          defaultValue={field.destination}
          disabled={conditionType === 'extension'}
        />
      </Box>
    </Box>
  );
}

// Componente para acciones por defecto
function IVRDefaultAction({ label, actionField, destinationField, register, watch, setValue, editingSkill, skillsList, dialplanList, configKey, disabled = false, disabledReason = '' }) {
  return (
    <Box sx={{ border: '1px solid #ddd', borderRadius: 1, p: 2 }}>
      <Typography variant="subtitle2" sx={{ mb: 1 }}>
        {label}
      </Typography>
      <Box display="grid" gridTemplateColumns="1fr 1fr" gap={1}>
        <IVRActionSelect
          register={register}
          actionField={actionField}
          destinationField={destinationField}
          watch={watch}
          skillsList={skillsList}
          defaultValue={editingSkill?.extra_config?.ivr_config?.default_actions?.[configKey]?.action}
          disabled={disabled}
        />
        <IVRDestinationField
          register={register}
          watch={watch}
          setValue={setValue}
          actionField={actionField}
          destinationField={destinationField}
          skillsList={skillsList}
          dialplanList={dialplanList}
          defaultValue={editingSkill?.extra_config?.ivr_config?.default_actions?.[configKey]?.destination}
          disabled={disabled}
        />
      </Box>
      {disabled && disabledReason && (
        <Typography variant="caption" color="text.secondary" sx={{ display: 'block', mt: 1 }}>
          {disabledReason}
        </Typography>
      )}
    </Box>
  );
}

// Componente para selector de acciones
function IVRActionSelect({ register, actionField, watch, defaultValue, disabled }) {
  return (
    <TextField
      select
      label="Acción"
      {...register(actionField)}
      defaultValue={defaultValue || 'hangup'}
      disabled={disabled}
      size="small"
    >
      <MenuItem value="dialplan">Dialplan</MenuItem>
      <MenuItem value="extension">Extensión</MenuItem>
      <MenuItem value="skill">Skill</MenuItem>
      <MenuItem value="cola_agentes">Cola de agentes</MenuItem>
      <MenuItem value="audio">Audio</MenuItem>
      <MenuItem value="nothing">No hacer nada</MenuItem>
      <MenuItem value="hangup">Colgar</MenuItem>
    </TextField>
  );
}

// Componente para campo de destino
function IVRDestinationField({ register, watch, setValue, actionField, destinationField, skillsList, dialplanList, defaultValue, disabled }) {
  const action = watch(actionField) || 'hangup'; // Default to hangup if undefined
  const currentValue = watch(destinationField);

  if (action === 'nothing' || action === 'hangup') {
    return <Box />; // Espacio vacío
  }

  if (action === 'skill') {
    return (
      <TextField
        select
        label="Skill"
        {...register(destinationField)}
        defaultValue={defaultValue || ''}
        disabled={disabled}
        size="small"
      >
        <MenuItem value="">Seleccionar skill...</MenuItem>
        {skillsList.map(skill => (
          <MenuItem key={skill.id} value={skill.skilldef}>
            {skill.skillname} ({skill.skilldef})
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (action === 'cola_agentes') {
    return (
      <TextField
        select
        label="Cola"
        {...register(destinationField)}
        defaultValue={defaultValue || 'current_skill'}
        disabled={disabled}
        size="small"
      >
        <MenuItem value="current_skill">Cola actual</MenuItem>
        <MenuItem value="other">Otra cola...</MenuItem>
      </TextField>
    );
  }

  if (action === 'dialplan') {
    return (
      <TextField
        select
        label="Dialplan"
        {...register(destinationField)}
        defaultValue={Number(defaultValue) || ''}
        disabled={disabled}
        size="small"
      >
        <MenuItem value="">Seleccionar dialplan...</MenuItem>
        {dialplanList.map(dp => (
          <MenuItem key={dp.id} value={dp.id}>
            {dp.dialplan || `Dialplan ${dp.id}`}
          </MenuItem>
        ))}
      </TextField>
    );
  }

  if (action === 'audio') {
    return (
      <AudioPathSelector
        value={currentValue || defaultValue || ''}
        onChange={(val) => setValue(destinationField, val)}
        label="Ruta audio"
        placeholder="enigma/audio1"
        helperText="Path del archivo de audio sin extensión"
        textFieldProps={{ size: 'small', disabled }}
      />
    );
  }

  // Para extension
  return (
    <TextField
      label="Extensión"
      {...register(destinationField)}
      defaultValue={defaultValue || ''}
      placeholder="1001"
      disabled={disabled}
      size="small"
    />
  );
}
